import type { FC, ReactNode } from "react";
import type { useForm, UseFormMethods } from "react-hook-form";
import type { DefaultTheme } from "styled-components";
import type { mutateCallback } from "swr/dist/types";
import type { MergeExclusive, ValueOf } from "type-fest";
import type { ChipType } from "../components/Chips/Chips";
import type { IIconProps } from "../components/Icons/Icons";
import type { INavStyles } from "../layout/shared";
import type {
  AttributeDataType,
  PIMProduct,
  ProductStatusType,
  TemplateType,
} from "./types.PIM";

// Type guards

/**
 * Typeguard for if value is defined, also works for undeclared values.
 * @param value
 */
export function isDefined<T>(value: T | undefined | null): value is T {
  return typeof value !== "undefined" && value !== null;
}

type EmptyString = "";
type EmptyArray = never[];
type EmptyObject = Record<string, never>;

type Empty = EmptyArray | EmptyObject | EmptyString;

/**
 * Typeguard for if value is empty. supports [], "" and {}.
 * @param subject
 */
export function isEmpty<T extends string | any[] | object>(
  subject: T | Empty
): subject is Bottom<T> {
  switch (typeof subject) {
    case "object":
      return Object.keys(subject).length === 0;
    case "string":
      return subject === "";
    default:
      return false;
  }
}

type Bottom<T> = T extends string
  ? EmptyString
  : T extends any[]
  ? EmptyArray
  : T extends object
  ? EmptyObject
  : never;

export function isChipTypeArray(value: any): value is ChipType[] {
  if (!isDefined(value)) return false;
  else if (isEmpty(value)) return false;
  else if (
    value.every((val: any) => Object.prototype.hasOwnProperty.call(val, "name"))
  ) {
    return true;
  } else return false;
}

export function isStringArray(value: any): value is string[] {
  if (!isDefined(value)) return false;
  else if (isEmpty(value)) return false;
  else if (value.every((val: any) => typeof val === "string")) return true;
  else return false;
}

// utility types:
/**
 * Make it explicit that that a component uses children
 *
 * usage:
 * ```
 * type DeletableChipProps = WithChildren<{
 *     active?: boolean;
 *     handleClick: (e: Event) => void;
 *   }>;
 *   ```
 */
export type WithChildren<T = {}> = T & { children?: React.ReactNode };

export type WithPagination<T = {}> = T & { pagination: IPagination };

export type OptionType<T = any> = {
  value: T;
  label: string;
};

/**
 * A little alias to make types more explicit.
 */
export type UUID = string;

/**
 * This is a string, just a nice alias to make things easier to read.
 * ISO 3166, Alpha2
 *
 * This corresponds to the `alpha_2` value in the @see ICountry type.
 * reference table:
 * {@link https://www.iban.com/country-codes}
 *
 */
export type Alpha2 = string;

/**
 * used in conjunction with `OptionType` with `react-select`. The __isNew__
 * boolean will be present only when using `CreatableSelectBox`
 */
export type CreatedOption<T = any> = {
  value: T;
  label: string;
  __isNew__?: boolean;
};

/**
 * Determine if the value is an option or created option.
 * This exists to guard against null value returns by react-select
 * @param maybeCreatedOption
 */
export function isCreatedOption(
  maybeCreatedOption: unknown
): maybeCreatedOption is CreatedOption {
  return (
    typeof maybeCreatedOption === "object" &&
    maybeCreatedOption !== null &&
    maybeCreatedOption?.hasOwnProperty("value") &&
    maybeCreatedOption?.hasOwnProperty("label")
  );
}

export interface UserConfigurableSEODetails {
  page_title: string;
  meta_title: string;
  meta_keywords: string;
  meta_description: string;
}

export interface ProductGroups {
  id: string;
  type: string;
  name: string;
  image_url: URL | null;
}

export interface Identifiers {
  molecular_formula: string | null;
  hs_code: string | null;
  synonyms: string | null;
  cas_number: string | null;
  ec_number: string | null;
  inchi_key: string | null;
  inci_name: string | null;
  chemical_name: string; // This can sometimes be "NA" or "N/A"
}

/**
 * Used for both product "property" and product "specification", which have
 * the same structure and are not likely to diverge.
 */
export interface ProductDetail {
  id?: string;
  unit: string;
  value: string;
  name: string;
}

/**
 * The type of the product application data that is attached to
 * products (product.product_applications).  It contains a `filter_id` but
 * `filter_id` is not currently documented in the API spec (2021/07/29).
 * For historical reasons the `filter_id` is what we want to use, not the `id`.
 * So we don't even include the `id` in the type, to prevent us from using it.
 *
 * To work around this issue and prevent making a bad situation worse, convert
 * this type to ProductApplication to fix the ID and convert into a corrected
 * type:
 *
 *    fixProductApplication(productApplicationBrokenId)
 *
 * Eventually it would be better if the backend just dropped the current `id`
 * property and renamed `filter_id` to `id`.
 *
 * @see ProductApplicationSchema
 */
export interface ProductApplicationBrokenId_DO_NOT_USE {
  // See comment above.
  // id: string;
  name: string;
  image_url: string | null;
  filter_id: string;
}

/**
 * The type of the product application data that is attached to a quote
 * item or quote request item (quoteItem.product_applications). It does not
 * contain a `filter_id`, but the `id` here is the same ID as the `filter_id`
 * on ProductApplication. (Even though the same ProductApplicationSchema
 * describes both sets of data.)
 * @see ProductApplicationSchema
 */
export interface ProductApplication {
  id: string;
  name: string;
  image_url: string | null;
}

export interface IPackagingType {
  name: string;
  id: string;
  description: string;
  // units_per_tl appears to be an unused hold over from version.
  units_per_tl: string | null;
}

/**
 * @see PackagingUnitSchema
 */
export interface IPackagingUnit {
  name: string;
  id: string;
}

/**
 * @see ProductSKUSchema
 * Existing products have `is_sample` initialized to null, which we are treating
 * as false in application code.
 *
 * The distributor_id is null for producer skus and will only have a value for
 * skus from a distributor.
 *
 */
export interface ProductSKU {
  id: string;
  distributor_id: string | null;
  erp_system_id: string;
  packaging_unit: IPackagingUnit;
  packaging_type: IPackagingType;
  package_description: string;
  package_volume: string;
  list_price: string;
  is_sample: boolean | null;
  buyer_id?: string;
  // 'Buyer SKU' is for custom SKUs created by a seller or a buyer, it has a
  // buyer ID property.
  // "Product SKU" is the standard SKU defined for a given product.
  // "No preference SKU" is used when the buyer selects "no preference" for a
  // SKU on a quote request. (see SkuNoPreference)
  kind: "Product SKU" | "Buyer SKU";
  number: string;
  sku_status?: string;
}

/**
 * This type of SKU ( @see ProductSKU ) is created for a cart or quote request
 * item where the user chooses "No Preference" for the SKU.
 * The only useful thing in it is the `packaging_unit` which is how we know the
 * unit of measure for the total quantity on the quote request item.
 * It gets its own type to help prevent its nullability from infecting all
 * other uses of SKUs.
 * Issa read this
 */
export interface SkuNoPreference {
  buyer_id: null;
  distributor_id: string | null;
  erp_system_id: null;
  id: string;
  is_sample: null;
  list_price: string;
  // name: null;
  package_description: null;
  package_volume: null;
  packaging_type: null;
  packaging_unit: IPackagingUnit;
  kind: "No preference SKU";
  number?: string;
}

/**
 * Request body for POST:
 * /v1/tenants/{buyer_tenant_id}/products/{product_id_from_quote}/custom-skus
 *
 * @see TenantSKUCreationArgsSchema
 */
export interface TenantSkuCreationArgs {
  erp_system_id?: string;
  custom_packaging_name?: string;
  packaging_type_id: string;
  packaging_unit_id: string;
  package_volume: string;
  list_price?: string;
  package_description?: string;
}

/**
 * Response body for GET /v1/storefronts/{storefront_id}/products/packaging-types
 * @see PackagingTypePaginatedOutputSchema
 * Note the name of the schema includes "paginated" but there is no "paginated"
 * property, only "data", and no "limit" param.
 */
export interface PackagingTypePaginatedOutput {
  data: IPackagingType[];
}

/**
 * Response body for GET /v1/storefronts/{storefront_id}/products/packaging-units
 * @see PackagingUnitPaginatedOutputSchema
 * Note the name of the schema includes "paginated" but there is no "paginated"
 * property, only "data", and no "limit" param.
 */
export interface PackagingUnitPaginatedOutput {
  data: IPackagingUnit[];
}

/**
 * Included with products and also attached to quotes, orders, sample requests,
 * etc.
 *
 * Success response (201) for POST to:
 * /v1/storefronts/{storefront_id}/products/{id}/documents/{kind}
 *
 * @see DocumentSchema
 */
export interface ProductDocument {
  kind:
    | "sds"
    | "tds"
    | "misc"
    | "purchase_order"
    | "invoice"
    | "application_guide"
    | "marketing_documents"
    | "customer_tax_exemption";
  name: string;
  size: number; // kilobytes?
  signed_url: string;
  id: string;
  is_downloadable: boolean;
  language?: SupportedLanguage;
}

export interface TDSDocument {
  kind: string;
  name: string;
  size: number; // kilobytes?
  signed_url: string;
  id: string;
}

/**
 * Associated with Orders from GET /v1/storefronts/{storefront_id_or_slug}/orders/{id}
 *
 * @see OrderDocumentSchema
 */
export type OrderDocument = {
  kind:
    | "invoice"
    | "shipping_document"
    | "purchase_order"
    | "other"
    | "packaging_list";
  name: string;
  size: number; // kilobytes?
  signed_url: string;
  id: string;
};

/**
 * Associated with Samples from GET /v1/storefronts/{storefront_id_or_slug}/samples/{id}
 */
export type SampleDocument = {
  kind:
    | "invoice"
    | "shipping_document"
    | "sample_request"
    | "other"
    | "packaging_list";
  name: string;
  size: number;
  signed_url: string;
  id: string;
};
export type OrderOrSampleDocument = OrderDocument | SampleDocument;

export type TransactionDocument = ProductDocument & {
  product_name: string;
  is_all_product: boolean;
  language: SupportedLanguage;
};

export type IExternalIdKind = "sap" | "ulp" | "salesforce";

/**
 * @see ExternalIdSchema
 */
interface IExternalId {
  external_id: string;
  kind: IExternalIdKind | null;
}

/**
 * @see SEODetailSchema
 */
export type SEODetail = {
  meta_description: string | null;
  meta_keywords: string | null;
  meta_title: string | null;
  page_title: string | null;
  og_title: string | null;
  og_type: string | null;
  og_image: string | null;
  og_description: string | null;
  og_locale: string | null;
  og_url: string | null;
};

// kind for orderDocuments = "invoice" | "shipping_document"
// invoice, shipping_document, purchase_order, other
// pdf doc txt json csv png jpeg doc dox svg webp

export type ProductLink = {
  id: UUID;
  url: string;
  name: string;
  is_visible: boolean;
};

/**
 * @see MasterProductSchema
 * @see SellerProductSchema
 */
export type Product = PIMProduct;

// {
//   slug: string;
//   safety_datasheet: string | null;
//   created_at: Date;
//   sold_by: string;
//   technical_datasheet: string | null;
//   meta_description: string | null;
//   functions: ProductTag[];
//   market_segments: ProductTag[];
//   industries: ProductTag[];
//   value_propositions: ValueProposition[];
//   product_groups: ProductGroups[];
//   identifiers: Identifiers | null;
//   meta_keywords: string | null;
//   produced_by: string | null;
//   product_properties: ProductDetail[];
//   product_specifications: ProductDetail[];
//   master_product: MasterProduct;
//   product_applications: ProductApplicationBrokenId_DO_NOT_USE[];
//   description: string;
//   brand_name: string;
//   meta_title: string;
//   id: string;
//   name: string;
//   is_active: boolean;
//   product_skus: ProductSKU[];
//   documents: ProductDocument[];
//   links: ProductLink[];
//   image_url: string; // Data from the backend may be an empty string ("").
//   is_featured: boolean | null;
//   external_id: IExternalId;
//   is_contracted_product: boolean | null;
//   seo_detail: SEODetail | null;
//   allow_custom_sku: boolean;
//   modified_at: string;
//   modified_by: string;
//   last_modified_full: LastModified;
// };

export interface LeadCartItem {
  packaging_type_id: string;
  packaging_unit_id: string;
  product: {
    name: string;
    identifiers?: Identifiers;
    produced_by?: string | null;
  };
  product_applications: ProductApplication[];
  total_volume: string;
}

export interface LeadCart {
  id: string;
  items: LeadCartItem[];
}

export interface IAddressInfo {
  city: string;
  country: string;
  state: string;
  postal_code: string;
  address1: string;
  address2?: string | null;
}

/**
 * A partial address used only with leads.
 */
export type LeadAddress = IAddressInfo & {
  state_display_name?: string;
  county?: string;
};

/**
 * @see LeadNoteSchema
 */
export type LeadNote = {
  id: UUID;
  message: string;
  creator_name: string;
  is_system_generated: boolean;
  created_at: string;
  note_type: string;
  created_by: string;
  message_for_buyer: string | null;
  modified_at: string;
  modified_by: string;
};

export type RejectionNote = {
  created_at: string;
  created_by: string;
  creator_name: string;
  id: UUID;
  message: string | null;
  message_for_buyer: string | null;
  modified_at: string;
  modified_by: string;
  note_type: string;
};

export type LeadRejectionArgsSchema = {
  internal_message: string | null;
  message_for_buyer: string | null;
  send_email_to_buyer: boolean;
};

export type LeadAssignee = {
  id: UUID;
  firstname: string;
  lastname: string;
  email_address: string;
  business_unit?: string;
  country?: Alpha2;
};

/**
 * This is the response for the GET registration/contact-us requests
 */
export interface Lead {
  buyer_company_name: string;
  buyer_email: string;
  buyer_first_name: string;
  buyer_last_name: string;
  buyer_phone_number: string;
  cart: LeadCart;
  created_at: string;
  id: string;
  assignee_id: UUID;
  assignee: LeadAssignee | null;
  assignment_date: string | null;
  modified_at: string;
  seller_id: string;
  status: LeadStatus;
  extra?: {
    message?: string;
    custom_choices?: Record<string, string | null>;
  };
  parent_lead_id: string;
  children: Lead[];
  address: LeadAddress | null;
  number: string;
  source: "contact_form" | "registration" | "quote_request";
  is_existing_customer: boolean;
  company_name: string;
  customer_id: string;
  parent_customer_id: string;
  shipping_address?: IAddress;
  billing_address?: IAddress;
  required_eta?: string;
  internal_notes: LeadNote[];
  rejection_note?: RejectionNote;
  region?: string;
  subscribers: LeadAssignee[];
  subscribers_updated_on?: string | null;
  subscriber_assignment_type?: "new" | "updated";
  lead_intel?: LeadIntel | null;
}

export interface LeadIntelCompanyProduct {
  product_name: string | null;
  product_description: string | null;
}

export interface LeadIntelCartProductInsight {
  product_name: string | null;
  usage_insights: string[] | null;
}

export interface LeadIntelOpportunityProduct {
  product_name: string | null;
  product_number: string | null;
}

export interface LeadIntelOpportunity {
  function: string | null;
  function_description: string | null;
  products: LeadIntelOpportunityProduct[] | null;
}

export interface LeadIntelSimilarCustomer {
  customer_name: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  overview: string | null;
  relevance: string | null;
  website: string | null;
}

export interface LeadIntel {
  id: string | null;
  customer_name: string | null;
  summary: string | null;
  website: string | null;
  linkedin: string | null;
  location: string | null;
  markets_served: string[] | null;
  contact_linkedin: string | null;
  contact_title: string | null;
  lead_company_products: LeadIntelCompanyProduct[] | null;
  cart_product_insights: LeadIntelCartProductInsight[] | null;
  opportunities: LeadIntelOpportunity[] | null;
  similar_customers: LeadIntelSimilarCustomer[] | null;
}

/**
 * There may or may not be production data with this type, however new features should not be built to use it.
 * @deprecated
 */
export type DeprecatedLeadStatus = "Lead Declined";

/**
 * Do not use "Lead Declined" in new features
 */
export type LeadStatus =
  | "New"
  | "Forwarded"
  | "Converted"
  | "Accepted"
  | "Added as User"
  | "Added as Address"
  | "Rejected"
  | DeprecatedLeadStatus
  | "blocked"
  | "Sent to Distributor"
  | "Rejected by Distributor"
  | "Accepted by Distributor"
  | "followed_externally";

/**
 * Success response (201 & 208) for POST to
 * /v1/storefronts/${storefront_id}/leads
 * A 201 response is a LeadSchema (has `source` property).
 * A 208 response is a LeadUserExistsSchema (has `redirect_url`).
 * This type is for both with `source` and `redirect_url` as optional.
 * @see ILeadSchema
 * @see LeadUserExistsSchema
 */
export type ProfileComparisonTargetType = "group" | "collection";

export type ProfileComparisonConfig = {
  id: string;
  template_id: string;
  target_type: ProfileComparisonTargetType;
  target_id: string;
  target_name: string;
  target_display_name: string;
  key_attribute_id: string | null;
  key_attribute_name: string | null;
  key_attribute_display_name: string | null;
};

export type ProfileComparisonConfigsResponse = {
  data: ProfileComparisonConfig[];
  count: number;
};

export type ProfileComparisonConfigInput = {
  target_type: ProfileComparisonTargetType;
  target_id: string;
  key_attribute_id?: string | null;
};

export interface ILeadSchema {
  id: string;
  cart: LeadCart;
  buyer: IBuyer;
  status: string; // TODO add enum.
  created_at: string;
  modified_at: string;
  source?: string;
  redirect_uri?: string;
}

/**
 * despite the name of the schema these are in fact quote request leads. This is
 * a bit of a historical quirk because originally all leads were "proto quote requests".
 * @see LeadPaginatedOutputSchema
 */
export type QuoteRequestLeadPaginatedOutput = WithPagination<{
  data: Lead[];
}>;

interface IBuyer {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  company_name: string;
}

export type FilterGroup = {
  count: number;
  image_url: string | null;
  name: string;
  selected: boolean;
  id?: string;
  // Attribute input_type for attribute facets (e.g. "user"), or
  // "status"/"template". "user" facets are applied by id; everything else
  // by name. Numeric facets are NOT described here — they live in the
  // separate `numeric_filters` map on `ProductFiltersMainSchema`, never in
  // this `FilterGroup`/chip-based `filters` map (see NumericFilterMeta).
  type?: string;
};

/**
 * AP-1016 (Advanced Filters) — quantitative/numeric filter operators, per
 * the Confluence spec's Operators & Match Rules
 * (https://agilischemicals.atlassian.net/wiki/spaces/EN/pages/1896841217/Advanced+Filters#Operators-%26-Match-Rules).
 * There are only four — `at_least`/`at_most` were folded into `between`
 * once Min/Max became independently optional (see `NumericFilterCriterion`):
 * a `between` criterion with only Min filled *is* "at least". Which
 * operators are actually offered for a given filter comes from the backend
 * response (`NumericFilterMeta.operators`) — never hardcode the list when
 * rendering.
 */
export type NumericFilterOperator =
  | "is"
  | "is_exactly"
  | "between"
  | "overlaps";

/**
 * All four numeric filter operators, in dropdown order. Product/MDM
 * listings get their operator list from the backend's
 * `numeric_filters[key].operators` instead — this constant is the fallback
 * for surfaces with no backend equivalent to source it from, namely
 * collection tables (matching runs client-side there, see
 * `matchesNumericFilter`). `is`/`is_exactly` and `between`/`overlaps` are
 * pairwise indistinguishable against Part 1's scalar-only stored data (they
 * only diverge once Part 2 introduces stored *ranges* to compare against),
 * but the list is kept complete here rather than trimmed to a
 * UX-recommended subset — see `NumericFilterOperator`'s doc comment.
 */
export const NUMERIC_FILTER_OPERATORS: NumericFilterOperator[] = [
  "is",
  "is_exactly",
  "between",
  "overlaps",
];

// Each variant carries a single literal `operator` so TypeScript can narrow
// both the positive and negative branches of `criterion.operator === "is"`
// checks — a union member whose discriminant is itself a multi-literal
// union doesn't narrow reliably in the `else`/negated branch.
//
// Per the Confluence spec's Operators & Match Rules, `between`/`overlaps`
// take Min & Max but the fields are independently optional — "fields left
// empty are not searched, only filled fields apply" — so a range with only
// Min is an open-ended "at least" query and only Max is "at most". At least
// one of the two must be present; both blank isn't a valid criterion. That
// invariant isn't expressible in the type (TS can't require "one of two
// optional fields") — it's enforced at runtime via `hasNumericBound`.
export type NumericFilterCriterion =
  | { operator: "is"; value: number }
  | { operator: "is_exactly"; value: number }
  | { operator: "between"; min?: number; max?: number }
  | { operator: "overlaps"; min?: number; max?: number };

/**
 * The backend's confirmed/parsed criterion actually applied to a request —
 * the numeric equivalent of an enum facet option's `selected` flag. `null`
 * when no numeric criterion is active for this filter, INCLUDING when one
 * was sent but silently rejected (malformed `_op`, both bounds missing on a
 * range, bad number format, inverted range, ...) — per the BE spec this is a
 * confirmation, not an echo, and must be checked rather than assumed to
 * mirror what was sent. Numbers arrive as strings (precision-safe over
 * JSON floats). `min`/`max` are independently optional, mirroring
 * `NumericFilterCriterion`.
 */
export type NumericFilterAppliedValue =
  | { op: NumericFilterOperator; value: string }
  | { op: NumericFilterOperator; min?: string; max?: string };

/** One entry of the `numeric_filters` response map — see `NumericFilters`. */
export type NumericFilterMeta = {
  /** Discriminator so other filter kinds (e.g. future date-range) can reuse this map. */
  type: "numeric";
  /** Which operators this filter accepts — populate the operator dropdown from this, never hardcode. */
  operators: NumericFilterOperator[];
  applied: NumericFilterAppliedValue | null;
};

/**
 * A sibling map to `filters` on `ProductFiltersMainSchema` (and the
 * equivalent MDM entity-type filters schema) — numeric filter keys never
 * appear in `filters`, only here. Keyed by the same `filter_type` slug
 * space as `filters` and `custom-labels[].filter_type`.
 */
export type NumericFilters = Record<string, NumericFilterMeta>;

export type FilterType =
  | "application"
  | "function"
  | "industry"
  | "group1"
  | "group2"
  | "group3"
  | "market_segment"
  | "proposition"
  | "produced_by"
  | "product_line";

export type FilterLabel = {
  language: SupportedLanguage;
  label: string;
};

/**
 * despite the name this is the response body from the custom-labels endpoint.
 * It has been slightly overloaded to include more related information to
 * control how the product detail page is displayed.
 */
export type TagClassificationConfig = {
  filter_type: string;
  filter_labels: FilterLabel[];
  is_visible: boolean;
};

/**
 * group1, group2, group3 and selected are legacy from 2.0.
 *
 */
export type Filters = Record<string, (FilterGroup & { name: string })[]>;

export type ProductFiltersMainSchema = {
  filters: Filters;
  /** AP-1016 (Advanced Filters, Part 1) — see `NumericFilters`. Absent/empty when nothing numeric is configured. */
  numeric_filters?: NumericFilters;
};

/**
 * AP-1016 (Advanced Filters, Part 2) — the Advanced Filters modal.
 * @see server/src/agilis/models/advanced_filters.py (AdvancedFilterCollectionInstance, AdvancedFilterQuantitativeFilter, AdvancedFilterOtherFilter)
 * @see server/src/agilis/views/storefront.py (admin CRUD + validation)
 * @see server/src/agilis/views/pim_product.py (listing-endpoint wiring)
 * @see docs/plans/AP-1016-advanced-filters-part2-BE-plan.md §1, §3
 * @see docs/plans/AP-1016-advanced-filters-part2-FE-plan.md
 */

/** Admin config CRUD — request shape for one Quantitative Filter card within a collection instance. */
export type AdvancedFilterQuantitativeFilterInput =
  | { kind: "single"; is_attribute_id: string }
  | {
      kind: "range";
      display_name: string;
      is_attribute_id?: string | null;
      min_attribute_id?: string | null;
      max_attribute_id?: string | null;
    };

/**
 * Read shape adds the resolved `display_name` (the mapped attribute's own
 * name for `single`, the stored name for `range`) and a server-driven
 * `operators` list — always the full four `NumericFilterOperator`s today,
 * per the same "never hardcode `operators`" contract as Part 1's
 * `NumericFilterMeta`. `is_valid` is computed read-time: false once any
 * attribute this filter references has been archived/removed.
 */
export type AdvancedFilterQuantitativeFilter =
  AdvancedFilterQuantitativeFilterInput & {
    id: string;
    display_name: string;
    operators: NumericFilterOperator[];
    is_valid: boolean;
  };

export type AdvancedFilterOtherFilterInput = { attribute_id: string };

export type AdvancedFilterOtherFilter = AdvancedFilterOtherFilterInput & {
  id: string;
  attribute_display_name: string;
  is_valid: boolean;
};

/** Admin config CRUD — the full (Collection, Key Attribute) instance body. PATCH is full-replace: always submit the complete current arrays, never a diff. */
export type AdvancedFilterCollectionInstanceInput = {
  collection_id: string;
  key_attribute_id: string;
  quantitative_filters: AdvancedFilterQuantitativeFilterInput[];
  other_filters: AdvancedFilterOtherFilterInput[];
};

export type AdvancedFilterCollectionInstance = {
  id: string;
  collection_id: string;
  collection_name: string;
  key_attribute_id: string;
  key_attribute_display_name: string;
  /** False once any referenced attribute (Key Attribute or a mapping) is archived/removed — instance-level rollup of every mapping's own `is_valid`. */
  is_valid: boolean;
  quantitative_filters: AdvancedFilterQuantitativeFilter[];
  other_filters: AdvancedFilterOtherFilter[];
};

/** One Key Attribute value in use on accessible products, with its product count — the modal's left-pane accordion leaves. */
export type AdvancedFilterKeyAttributeValue = {
  value: string;
  product_count: number;
};

export type AdvancedFilterKeyAttributeValuesResponse = {
  values: AdvancedFilterKeyAttributeValue[];
  total: number;
};

// Written as a pre-distributed union (`(A&B)|(A&C)`) rather than
// `{quantitative_filter_id} & ({op:...}|{op:...})` — TypeScript's narrowing
// doesn't reliably distribute an intersection over a union when checking
// `criterion.op` (see `NumericFilterCriterion`'s own doc comment above for
// the sibling gotcha with multi-literal discriminants); this shape narrows
// correctly in both directions on a plain `criterion.op === "..."` check.
export type AdvancedFilterQuantitativeCriterion =
  | { quantitative_filter_id: string; op: "is" | "is_exactly"; value: string }
  | {
      quantitative_filter_id: string;
      op: "between" | "overlaps";
      min?: string;
      max?: string;
    };

export type AdvancedFilterOtherCriterion = {
  other_filter_id: string;
  values: string[];
};

/**
 * One right-pane criterion card — the request payload's per-card shape.
 * Row-scoped: the Key Attribute value and every filled quantitative/other
 * condition must match within the same attribute_value_row. Keyed by
 * `quantitative_filter_id`/`other_filter_id` (the mapping tables' own ids),
 * never by attribute id/name — those are the query engine's only
 * unambiguous keys.
 */
export type AdvancedFilterCard = {
  collection_instance_id: string;
  key_attribute_value: string;
  quantitative: AdvancedFilterQuantitativeCriterion[];
  other: AdvancedFilterOtherCriterion[];
};

/**
 * Mirrors Part 1's `NumericFilterAppliedValue` confirmation semantics
 * exactly: a card (or a field within one) the backend silently dropped
 * (deleted mapping, bad op, unparseable number, inverted range) is simply
 * absent here — diff against what was sent, never assume an echo. Every
 * number comes back as a string, never a JSON float.
 */
export type AdvancedFiltersAppliedCard = AdvancedFilterCard;

export type StorefrontEdition =
  | "starter"
  | "business"
  | "enterprise"
  | "pim"
  | "mdm";

export type TenantType = "Distributor" | "Buyer" | "Producer" | "Admin";

export type PolicyDocument = {
  kind: "privacy_policy" | "terms_of_sale";
  name: string;
  size: number;
  signed_url: string;
  id: string;
};

export type CustomFont = {
  font_url: string;
  font_family_name: string;
};

export type HomepageSettings = {
  mx_width: string | undefined;
  nav_settings: {
    [key: string]: INavStyles;
  };
};

/**
 * @see LeadTransactionVisibility
 * server/src/agilis/models/constant.py
 *
 */
export type LeadTransactionVisibility = "enabled" | "disabled" | "hidden";

/**
 * This is actually editable in flask admin, although it shouldn't be. contact-us with the dash is intentional,
 * its a configuration workaround for a bug involving duplicate tabs outside login
 */
export type DefaultPublicNavRoute =
  | "contact-us"
  | "registration"
  | "portfolio"
  | "home";

export type PublicNavRouteConfig = {
  route: DefaultPublicNavRoute;
  enabled: boolean;
  translations: Record<SupportedLanguage, string>;
};

export type RouteConfiguration = PublicNavRouteConfig[];

export type ConfigureCheckboxItem = {
  id: UUID;
  name: string;
  quote: boolean;
  sample: boolean;
  contact: boolean;
  registration: boolean;
};
/**
 * @deprecated
 */
export type PortfolioViewType = "classic" | "modern" | "grid";

export type SsoProviderType = "ADFSSAML" | "GoogleOAuth";

export type LeadFormOtpLeadType =
  | "quote_request"
  | "registration"
  | "contact_us"
  | "sample_request";

export type LeadOtpSettings = {
  code_length: number;
  code_expiry_minutes: number;
  resend_cooldown_seconds: number;
  max_wrong_attempts: number;
  max_sends_per_hour: number;
};

export type LeadOtpInitResponse = {
  otp_session_id: string;
  email: string;
  resend_available_at: string;
  expires_at: string;
};

/**
 * /v1/storefronts/{storefront_id_or_slug}/metadata
 */
export type StorefrontMetaData = {
  // The `id` is the storefront ID.
  id: string;
  header: string;
  accepts_payments: boolean;
  sub_header?: string;
  sender_name?: string;
  placeholder: string;
  browser_title: string;
  tenant_name: string;
  metadata_localization: StoreFrontMetadataLocalization;
  tenant_type: TenantType | null;
  tenant_id: string;
  edition: StorefrontEdition;
  sample_requests_visibility: LeadTransactionVisibility;
  quote_requests_visibility: LeadTransactionVisibility;
  policy_documents: PolicyDocument[];
  configured_checkboxes: ConfigureCheckboxItem[];
  google_analytics_key: string | null;
  enable_discount_codes: boolean;
  enable_payments_on_sample_requests: boolean;
  enable_invoice_auto_generation: boolean;
  enable_transaction_lead_quote: boolean;
  enable_transaction_lead_sample: boolean;
  enable_order_auto_accept: boolean;
  enable_vertex_ai_search: boolean;
  enable_generic_sku: boolean;
  enable_approval_workflow: boolean;
  enable_barentz_api_integration: boolean;
  generic_sku_packaging_type_name: string;
  generic_sku_packaging_type_id: string | null;
  enable_product_creation_via_form: boolean;
  unlisted_product_requests_enabled: boolean;
  host: string | null;
  slug: string | null;
  release_version: string;
  custom_font?: CustomFont | undefined;
  theme_object: {
    version: string;
    branding: {
      brandColor: string;
      portalLogoUrl: string;
      publicLogoUrl: string;
      customFontFamily: string;
      favIconUrl: string;
    };
    designTokens: any;
  };
  homepage_settings?: HomepageSettings | undefined;
  custom_domain?: string;
  custom_pages?: CustomPageConfig[];
  digital_marketing_enabled: boolean;
  mandatory_customer_creation_form: boolean;
  default_currency: CurrencyCode;
  default_language: SupportedLanguage;
  use_custom_packaging: boolean;
  use_custom_privacy_policy: boolean;
  use_custom_terms_of_sale: boolean;
  supported_languages: { [key: string]: string };
  supported_languages_localized: { [key: string]: string };
  email_preferences_enabled: boolean;
  enable_product_selector: boolean;
  enable_reference_entities: boolean;
  enable_customer: boolean;
  enable_supplier: boolean;
  enable_distributor: boolean;
  enable_customer_portal: boolean;
  mdm_entity_display_names?: {
    customer: { singular: string; plural: string };
    supplier: { singular: string; plural: string };
    distributor: { singular: string; plural: string };
  };
  is_product_selector_enabled: boolean;
  product_selector_csv_upload_enabled: boolean;
  product_selector_form_inputs_enabled: boolean;
  product_selector_csv_document: ProductSelectorCSVDocument | null;
  route_configuration: RouteConfiguration;
  is_contact_message_compulsory: boolean;
  mandatory_registration_message: boolean;
  default_portfolio_view: PortfolioViewType;
  show_privacy_policy_on_contact_forms: boolean;
  sso_provider: SsoProviderType | null;
  sso_domain: string | null;
  sso_only: boolean;
  custom_support_url: string | null;
  totp_required: boolean;
  only_bulk_upload_enabled: boolean;
  object_reference_enabled: boolean;
  is_chatbot_configured: boolean;
  enable_agentic_search: boolean;
  enable_email_reminder: boolean;
  require_captcha: boolean;
  publish_completion_score_threshold: number;
  customer_publish_completion_score_threshold: number;
  supplier_publish_completion_score_threshold: number;
  distributor_publish_completion_score_threshold: number;
  enable_team_product_permissions: boolean;
  enable_list_prices: boolean;
  enable_product_reminder: boolean;
  /** AP-1016 (Advanced Filters, Part 2) — Agilis-internal only, no tenant-facing toggle. Sole gate for the Advanced Filters modal and its Portfolio Controls config section. */
  enable_advanced_filters: boolean;
  enable_profile_comparison: boolean;
  alternate_logo: boolean;
  max_number_of_filter_settings: number;
  enable_product_asset_search: boolean;
  google_verification_meta_name: string;
  google_verification_content: string;
  enable_fuzzy_match: boolean;
  enable_external_assets: boolean;
  lead_otp_settings: LeadOtpSettings | null;
  enable_share_assets_via_email?: boolean;
  enable_attribute_level_role_based_edit_permissions: boolean;
  enable_change_requests: boolean;
};

export type StorefrontFormData = {
  data: {
    Contact: {
      Description: string;
    };
    Register: {
      Description: string;
    };
  };
  language: SupportedLanguage;
};

export type SingleStorefrontLocalization = {
  browser_title: string;
  header: string;
  placeholder: string;
  is_active: boolean;
  sub_header: string;
  // localization_name: string;
};

/**
 * This is an object with keys based on ISO 639-1 or null. The reason we don't
 * have a union of supported languages is that it would require code changes
 * everytime a new one is added to stay accurate. Any language should be drop in supported here
 * although rtl languages would require additional changes to look good.
 */
export type StoreFrontMetadataLocalization = {
  [key: string]: SingleStorefrontLocalization;
} | null;

export interface CustomPageContent {
  id: string;
  browser_title: string | null;
  language: string;
  nav_link_text: string | null;
  is_active: boolean;
}

export interface CustomPageBanner {
  index: number;
  image_url: string;
  title: string;
  caption: string;
  link_url: string;
}

export interface CustomPageBannerSettings {
  fixed_overlay: boolean;
  title: string;
  caption: string;
  link_url: string;
}

export interface CustomPageConfig {
  path: string;
  nav_link_text: string | null;
  nav_link_icon: string | null;
  layout_omit_user_area: boolean;
  layout_omit_header: boolean;
  layout_full_width: boolean;
  is_system: boolean;
  priority: number;
  // New fields for nested navigation
  is_parent?: boolean;
  parent_path?: string;
  page_banner: CustomPageBanner[];
  page_banner_settings: CustomPageBannerSettings | null;
  page_content: CustomPageContent[];
}

export type CurrencyCode = "USD" | "CNY" | "JPY" | "CAD" | "EUR";

/**
 * @example { code: "USD", name: "US Dollar", symbol: "$" }
 */
export type Currency = {
  code: CurrencyCode;
  name: string;
  symbol: string;
};

/**
 * Response body for GET /v1/storefronts/{storefront_id}/metadata
 */
export type APIMetaData = {
  default_logo_url: string;
  default_redirect: string;
  default_from_email: string;
  error_logging_api_key: string;
  release_version: string;
  supported_currencies: Currency[];
  tenant_slug: string;
  captcha_site_key: string;
};

/**
 * These are the values of `user.user_type`. Some date from v2 and are needed
 * for backwards compatibility with v2. Others are used in v3 and  are
 * equivalent to the `user.rbac_role` values.
 * TODO what is the continued purpose of this?
 */
export type UserType =
  // v2 types - deprecated
  | "Primary Contact"
  | "Master Admin"
  | "Standard User"
  // v3 types
  | "Agilis Admin"
  | "Buyer Admin"
  | "Distributor Admin"
  | "Buyer Standard"
  | "Seller Admin"
  | "Seller Standard"
  | "Distributor";

/**
 * Used to show chips (options) when creating or editing a user account.
 * The `id` gets sent to the backend and `name` is shown in the UI.
 */
export type UserTypeChip =
  | { name: "Admin"; id: "Seller Admin" | "Buyer Admin" }
  | { name: "Distributor"; id: "Distributor Admin" }
  | { name: "Standard"; id: "Seller Standard" | "Buyer Standard" };

export type RBACRole =
  | "buyer_admin"
  | "buyer_standard"
  | "seller_admin"
  | "agilis_admin"
  | "distributor"
  | "distributor_admin"
  | "seller_standard"
  | "guest"
  | "invited_distributor";

/**
 * These typically control whether or not the route itself is visible on the
 * navbar, but may also eventually control the presence of tabs or subsections
 * of pages.
 */
export type ViewPermissions =
  | "view_lists"
  | "view_attributes"
  | "view_collections"
  | "view_groups"
  | "view_products"
  | "view_templates"
  | "view_assets"
  | "view_suppliers"
  | "view_customers"
  | "view_distributors"
  | "view_product_type_master"
  | "view_product_type_variant"
  | "view_product_type_standard";

/**
 * modify is always "create" AND "update"/"edit"
 * A user can never create something but not modify it.
 * controls wether creation/edit buttons are clickable.
 */
export type ModifyPermissions =
  | "modify_attributes"
  | "modify_lists"
  | "modify_groups"
  | "modify_collections"
  | "modify_products"
  | "modify_templates"
  | "modify_assets"
  | "modify_suppliers"
  | "modify_customers"
  | "modify_distributors"
  | "modify_product_type_master"
  | "modify_product_type_variant"
  | "modify_product_type_standard";

export type DeletePermissions =
  | "delete_lists"
  | "delete_attributes"
  | "delete_groups"
  | "delete_collections"
  | "delete_templates"
  | "delete_products"
  | "delete_assets"
  | "delete_suppliers"
  | "delete_customers"
  | "delete_distributors"
  | "delete_product_type_master"
  | "delete_product_type_variant"
  | "delete_product_type_standard";

export type PublishPermissions =
  | "publish_products"
  | "unpublish_products"
  | "publish_suppliers"
  | "unpublish_suppliers"
  | "publish_customers"
  | "unpublish_customers"
  | "publish_distributors"
  | "unpublish_distributors"
  | "publish_product_type_master"
  | "unpublish_product_type_master"
  | "publish_product_type_variant"
  | "unpublish_product_type_variant"
  | "publish_product_type_standard"
  | "unpublish_product_type_standard";

export type Permission =
  | ViewPermissions
  | ModifyPermissions
  | DeletePermissions
  | PublishPermissions;

export type TwoFactorTypes = "totp" | "sms" | "voice";

export interface TOTPSchema {
  has_totp_key: boolean;
  preferred_2fa_type: TwoFactorTypes | null;
  totp_last_authorized: string | null;
  totp_login_required: boolean;
  sms_2fa_phone_number: string | null;
}

interface UserBase extends TOTPSchema {
  /**
   * Only on buyer rows, where it is the buyer's `Tenant.name`.
   * @see server/src/agilis/models/odm.py SellerCustomerUserSchema
   */
  company_name?: string | null;
  firstname: string;
  id: string;
  lastname: string;
  phone_number: string;
  email_address: string;
  position: string;
  tenant_id: string;
  user_type: UserType;
  username: string;
  created_at: string;
  is_active: boolean;
  role: { id: UUID; name: string };
  seller_id: string | null;
  settings: ITenantCustomerSettings | null;
  preferred_language: SupportedLanguage;
  quote_threshold_usd: number | null;
  order_threshold_usd: number | null;
  quote_threshold_local: number | null;
  order_threshold_local: number | null;
  is_primary_contact?: boolean;
  is_point_of_contact?: boolean;
  point_of_contact_id?: string;
  chat_email_notification_enabled: boolean;
  is_buyer_activated: boolean;
  self_actions_email_notification_enabled: boolean;
  team_actions_email_notification_enabled: boolean;
  products_view_mode: "list" | "portfolio" | "modern";
  email_notifications: {
    storefront_id: UUID;
    // leads
    leads: boolean;
    leads_enable_quote_request: boolean;
    leads_enable_sample_request: boolean;
    leads_enable_registration: boolean;
    leads_enable_contact_us: boolean;
    // transactions
    transactions: boolean;
    transactions_enable_quote_request: boolean;
    transactions_enable_order: boolean;
    transactions_enable_sample_request: boolean;
  }[];
  crm_id?: string;
  is_sso_login: boolean;
  tenant_user_contact_info?: AccountManagerContactInfo;
  country?: Alpha2;
  business_unit?: string;
}

interface SellerUser extends UserBase {
  rbac_role: Extract<
    RBACRole,
    "seller_admin" | "seller_standard" | "agilis_admin"
  >;
  permissions_list: Permission[];
}

interface BuyerUser extends UserBase {
  rbac_role: Exclude<
    RBACRole,
    "seller_admin" | "seller_standard" | "agilis_admin"
  >;
  permissions_list: never;
}

/**
 * Response for GET to:
 * /v1/storefronts/{storefront_id_or_slug}/users/login
 *
 * @see AgilisUserLoginSchema
 * Note this API schema needs to be updated to include email_address,
 * created_at, and is_active to match what the backend code is sending.
 *
 * (There is also the similar @see AgilisUserSchema and we may eventually want
 * to have a separate type for that.)
 *
 * The `user_type` is mostly a v2 thing that is superseded by RBAC roles.
 * It's primary purpose in v3 is for backwards compatibility with v2. However,
 * when creating or editing a user account, the back end takes a user_type
 * and uses that to calculate the rbac_role. The two categories are basically
 * equivalent for new v3 user accounts.
 *
 * For a buyer user the `seller_id` is the seller for that buyer on the
 * current storefront. That seller can be the storefront tenant (e.g. a
 * producer) or an invited distributor selling chemicals on that storefront.
 * For non invited distributor seller users the seller_id is the same as
 * the tenant_id.
 *
 *
 * Temporarily RBAC permissions only applies to sellers. Hopefully this code
 * doesn't last very long.
 */
export type User = SellerUser | BuyerUser;

/**
 * Response body for GET to:
 * /v1/storefronts/{storefront_id_or_slug}/tenants/{tenant_id}/users
 * @see AgilisUserPaginatedOutputSchema
 */
export type AgilisUsersPaginatedOutput = WithPagination<{
  data: User[];
}>;

/**
 * a potential request body for POST /v1/storefronts/{storefront_id_or_slug}/tenants/{id_or_slug}/users
 * if the user_type is "Standard Seller" other fields are required.
 * @see AgilisUserCreationArgsSchema
 */
export type AgilisUserCreationArgs = {
  email_address: string;
  firstname: string;
  lastname: string;
  phone_number: string;
  user_type: UserType;
  preferred_language: SupportedLanguage;
};

/**
 * The request body for POST /v1/storefronts/{storefront_id_or_slug}/tenants/{id_or_slug}/users
 * in the case the the `user_type` = "Standard Seller"
 * @see AgilisUserCreationArgsSchema
 */
export type AgilisUserCreationsSellerStandardArgs = AgilisUserCreationArgs & {
  user_type: "Seller Standard";
  // user_role: "seller_standard";
  quote_threshold_usd: number | null;
  order_threshold_usd: number | null;
  quote_threshold_local: number | null;
  order_threshold_local: number | null;
};

/**
 * The request body for PATCH /v1/storefronts/{storefront_id_or_slug}/tenants/{id_or_slug}/users/{user_id}
 * Currently the role_id param is required for sellers but is not needed for
 * other user types.
 * @see AgilisUserPatchArgsSchema
 */
export interface IAgilisUserPatchArgs {
  email_address?: string;
  firstname?: string;
  lastname?: string;
  crm_id?: string;
  phone_number?: string;
  preferred_language: SupportedLanguage;
  user_type?: UserType;
  role_id?: UUID;
  is_active?: boolean;
  quote_threshold_usd?: number | null;
  order_threshold_usd?: number | null;
  quote_threshold_local?: number | null;
  order_threshold_local?: number | null;
  email_notifications?: {
    storefront_id: UUID;
    // leads
    leads: boolean;
    leads_enable_quote_request: boolean;
    leads_enable_sample_request: boolean;
    leads_enable_registration: boolean;
    leads_enable_contact_us: boolean;
    // transactions
    transactions: boolean;
    transactions_enable_quote_request: boolean;
    transactions_enable_order: boolean;
    transactions_enable_sample_request: boolean;
  };
  country?: string;
  business_unit?: string;
}

export type IPagination = {
  offset: number;
  order_by: "desc" | "asc";
  total: number;
  limit: number;
};

type AddressTypes =
  | "Headquarters Address"
  | "Payer Address"
  | "Sold To Address"
  | "Warehouse";

/**
 * A single point of contact on an address. Either linked to an active
 * tenant user (`point_of_contact_user_id` set, `is_manual` false) or a
 * manually-entered contact (`is_manual` true).
 * @see PointOfContact
 */
export interface PointOfContact {
  id: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_name: string;
  email_address: string;
  phone_number: string;
  point_of_contact_user_id?: string;
  is_manual: boolean;
  /**
   * Both come from the linked user and are null for manual POCs.
   * @see server/src/agilis/models/odm.py PointOfContactSchema
   */
  business_unit?: string | null;
  country?: Alpha2 | null;
}

/**
 * because company_name was a later addition many existing addresses will
 * not have it.
 * @see AddressSchema
 */
export interface IAddress extends IAddressInfo {
  id: string;
  company_name: string | null;
  tax_id: string | null;
  crm_id: string | null;
  external_identity: IExternalId;
  state_display_name: string;
  subdivision_name?: string;
  county: string | null;
  type: AddressTypes;
  is_active: boolean;
  same_as_hq?: boolean;
  points_of_contact: PointOfContact[];
}

export interface Tenant extends IAddressInfo {
  primary_contact_id?: string;
  id: string;
  name: string;
  doing_business_as: string;
  external_identity: IExternalId;
  type: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  distributors: string[] | null; // array of hashes
  producers: string[]; // array of hashes
  addresses: IAddress[];
  market_segment: string[];
  industries: IIndustry[];
  point_of_contact_id: string;
  crm_id?: string;
  primary_contact: {
    full_name: string;
    first_name: string;
    last_name: string;
    email_address: string;
    phone_number: string;
    preferred_language: SupportedLanguage;
  };
  // This can be null because it was introduced well after this endpoint was
  // first built.
  modified_at: string | null;
}

interface IIndustry {
  id: string;
  name: string;
  image_url: string;
}

/**
 * /v1/storefronts/{storefront_id_or_slug}/tenants/{id_or_slug}/storefront/theme
 */
export interface ThemeResponse {
  id: string;
  tenant_id: string;
  theme_object: DefaultTheme;
}

/**
 * POST /v1/storefronts/${storefront_id}/leads/contact-requests
 * @see LeadCreationContactRequestArgsSchema
 */
export interface IContactRequest {
  seller_id_or_slug: string;
  message: string | undefined;
  buyer_first_name: string;
  buyer_last_name: string;
  buyer_email: string;
  buyer_phone_number: string;
  buyer_company_name: string;
  language: SupportedLanguage;
  custom_choices?: Record<string, string | null>;
  recaptcha_key?: string;
  captcha_token?: string;
}

export interface IRegistrationRequest {
  seller_id_or_slug: string;
  buyer_first_name: string;
  buyer_last_name: string;
  buyer_email: string;
  buyer_phone_number: string;
  buyer_company_name: string;
  message?: string;
  language: SupportedLanguage;
  custom_choices?: Record<string, string | null>;
  recaptcha_key?: string;
  captcha_token?: string;
}

export interface IRegistrationRequestWithAddress extends IRegistrationRequest {
  address: IAddressInfo & { county: ValueOf<Pick<IAddress, "county">> };
}
/**
 * GET /v1/storefronts/{id}/lead-configuration
 * TODO: add reference to backend schema object.
 *
 * `default_country` refers to the actual country, and `default_country_code` is
 *  used to look up the correct country code prefix e.g. "+1" for United States.
 */
export type LeadConfiguration = {
  show_location_form: boolean;
  default_country: Alpha2;
  default_country_code: Alpha2;
};

/**
 * POST /v1/storefronts/${storefront_id}/leads/contact-requests
 * @see LeadCreationContactRequestArgsSchema
 * @see LeadConfiguration
 * Special extension of the contact request schema for storefronts that have
 * optionally configured to have a partial address included in the lead for ease
 * of routing to distributors by location.
 */
export interface IContactRequestWithAddress extends IContactRequest {
  address: IAddressInfo & { county: ValueOf<Pick<IAddress, "county">> };
}

/**
 * Response for:
 * GET /v1/storefronts/{storefront_id_or_slug}/tenants
 * GET /v1/storefronts/{storefront_id_or_slug}/distributors
 * @see TenantPaginatedOutputSchema
 */
export interface ITenantPaginatedOutputResponse {
  data: Tenant[];
  pagination: IPagination;
}

/**
 * GET /v1/storefronts/{storefront_id_or_slug}/products/{product_id}/privacy
 */
export interface ISellerProductPrivacyResponse {
  id: string;
  is_private: boolean;
  visible_tenants: Tenant[];
}

/**
 * PATCH /v1/storefronts/{storefront_id_or_slug}/products/{product_id}/privacy
 */
export interface ISellerProductPrivacyArgs {
  is_private: boolean;
  tenants_to_add?: string[]; // array of tenant IDs
  tenants_to_delete?: string[]; // array of tenant IDs
}

/**
 * GET /v1/storefronts/{storefront_id}/products
 */
export interface IMasterProductPaginatedOutput {
  data: Product[];
  pagination: IPagination;
}

/**
 * Response body for GET /v1/storefronts/{id_or_slug}/products
 * @see SellerProductPaginatedOutputSchema
 */
export interface ISellerProductPaginatedOutput {
  data: Product[];
  pagination: IPagination;
}

/**
 * @see PaymentTermSchema
 */
export interface IPaymentTerm {
  id: string;
  name: string;
}

/**
 * Response body for:
 * GET /v1/storefronts/{storefront_id}/payment-terms
 * GET /v1/commerce/payment-terms
 * @see PaymentTermPaginatedOutputSchema
 */
export type PaymentTermPaginatedOutput = WithPagination<{
  data: IPaymentTerm[];
}>;

/**
 * @see PaymentModeSchema
 */
export interface IPaymentMode {
  id: string;
  name: string;
  payments_enabled: boolean;
}

export interface IMessageRequestJson {
  message?: string;
  message_type?: string;
  required_eta?: string;
  no_of_units?: string;
  is_tax_exempt?: string;
  valid_to_date?: string;
  fees_to_add?: Fee[];
  fees_to_overwrite?: Fee[];
  price_per_unit?: string | null;
  alternative_po_number?: string | null;
  kind?: string | null;
}

/**
 * Response body for:
 * GET /v1/storefronts/{storefront_id}/payment-modes
 * GET /v1/commerce/payment-modes
 *
 * @see PaymentModePaginatedOutputSchema
 */
export type PaymentModePaginatedOutput = WithPagination<{
  data: IPaymentMode[];
}>;

/**
 * @see DeliveryTermSchema
 */
export interface IDeliveryTerm {
  id: string;
  name: string;
  description: string;
}
/**
 * Response body for:
 * GET /v1/storefronts/{storefront_id}/delivery-terms
 * GET /v1/commerce/delivery-terms
 * @see DeliveryTermPaginatedOutputSchema
 */
export type DeliveryTermPaginatedOutput = WithPagination<{
  data: IDeliveryTerm[];
}>;

/**
 * Request body for POST to:
 * /v1/storefronts/{storefront_id_or_slug}/unified-cart:
 *
 * @see UnifiedCartArgSchema
 */
export type UnifiedCartArg = {
  product_id: string | null;
  unlisted_product_name?: string;
  applications?: string[];
  custom_application?: string;
  seller_id?: string;
  sku_id?: string;
  no_of_units?: string;
  price_per_unit?: string;
  currency?: CurrencyCode;
  shipping_address_id?: string;
  delivery_term_id?: string;
  payment_term_id?: string;
  payment_mode_id?: string;
  order_id?: string;
  item_id?: string;
  total_quantity?: string;
  packaging_unit_id?: string;
  custom_packaging_type_id?: string;
  custom_packaging_quantity?: string;
  buyer_id?: string;
  discount_code?: string;
};

/**
 * Request body for PATCH to:
 * /v1/storefronts/{storefront_id_or_slug}/unified-cart/items/{item_id}:
 *
 * @see UnifiedCartItemPatchArgsSchema
 */
export type UnifiedCartItemPatchArgs = {
  no_of_units?: string;
  price_per_unit?: string;
  currency?: CurrencyCode;
  applications?: string[];
  custom_application?: string;
  sku_id?: string;
  total_quantity?: string;
  packaging_unit_id?: string;
  custom_packaging_type_id?: string;
  custom_packaging_quantity?: string;
  buyer_id?: string;
};

/**
 * Response body for GET /v1/storefronts/{storefront_id_or_slug}/quotes
 * @see QuoteRequestPaginatedOutputSchema
 */
export interface IQuotesAPIResponse {
  data: IQuoteRequest[];
  pagination: IPagination;
}

/**
 * Response body for POST /v1/storefronts/{storefront_id_or_slug}/quotes
 * Response body for POST /v1/storefronts/{storefront_id_or_slug}/quotes/{id}
 * @see QuoteRequestSchema
 */
export interface IQuoteRequest {
  id: string;
  payment_term: IPaymentTerm;
  payment_mode: IPaymentMode;
  delivery_term: IDeliveryTerm;
  shipping_address: IAddress;
  shipping_point_of_contact?: PointOfContact | null;
  required_eta: string; // date-time string, e.g. "2020-08-01T17:32:28Z"
  items: QuoteRequestItem[];
  modified_at: string;
  fees: Fee[];
  discounts: IDiscountCodesSchema[];
  total_fees: string;
  valid_to_date: string;
  seller_id: string;
  seller_name: string;
  buyer_id: string;
  buyer_name: string;
  status: QuoteStatus;
  number: string;
  seller_address: IAddress;
  unread_message_count?: number;
  total_discounts: string;
  total_after_discount: string;
  account_manager_name: string;
  customer_service_rep_name: string;
}

/**
 * Request body for POST /v1/storefronts/{storefront_id_or_slug}/quotes
 * A POST to that endpoint creates a new cart and adds one item to it.
 * @see QuoteRequestCreationArgsSchema
 *
 * The properties:
 * - shipping_address_id
 * - delivery_term_id
 * - payment_term_id
 * Are optional here so they match the schema on the backend, but on the
 * frontend we typically always create quote requests by supplying these
 * properties. (Verify that in the code in case this comment is outdated.)
 */
export interface IQuoteRequestCreationArgs {
  seller_id: string;
  product_id: string;
  sku_id?: string;
  no_of_units?: string;
  price_per_unit?: string | null;
  currency?: CurrencyCode;
  applications?: string[];
  shipping_address_id?: string;
  delivery_term_id?: string;
  payment_term_id?: string;
  payment_mode_id?: string;
}

/**
 * Request body for PATCH /v1/storefronts/{storefront_id_or_slug}/quotes/{quote_id}
 * Request body for PATCH /v1/storefronts/{storefront_id_or_slug}/quotes/{quote_id}/respond
 * Used when an existing user (e.g. buyer) submits a cart.
 *
 * fees_to_remove is an array of UUIDs.
 *
 * fees_to_add and fees_to_remove are considered legacy. fees_to_overwrite
 * is easier to work with and should be used going forward. The other two
 * are only here to keep the type accurate.
 *
 * @see QuoteRequestPatchArgsSchema
 */
export interface QuoteRequestPatchArgs {
  delivery_term_id?: string;
  payment_term_id?: string;
  payment_mode_id?: string;
  shipping_address_id?: string;
  shipping_point_of_contact_id?: string;
  required_eta?: string;
  valid_to_date?: string;
  is_tax_exempt?: boolean;
  fees_to_add?: Fee[];
  fees_to_overwrite?: Fee[];
  fees_to_remove?: string[];
  discount_code?: string | null;
}

export interface QuoteRequestOrOrderItem {
  id: string;
  total_quantity: string;
  price_per_unit: string | null;
  currency: CurrencyCode;
  product_applications: ProductApplication[];
  documents: ProductDocument[];
  last_purchase: LastPurchase;
}

/**
 * Response body for POST /v1/storefronts/{storefront_id_or_slug}/quotes/{id}/items
 * Response body for PATCH /v1/storefronts/{storefront_id_or_slug}/quotes/{quote_id}/items/{item_id}
 * @see QuoteRequestDetailSchema
 */
export interface QuoteRequestItem {
  id: string;
  total_quantity: string;
  requested_total_quantity: string;
  no_of_units: string;
  requested_no_of_units: string;
  custom_packaging_quantity: string;
  price_per_unit: string | null;
  currency: CurrencyCode;
  sku: ProductSKU | SkuNoPreference;
  requested_sku: ProductSKU | SkuNoPreference | null;
  product_id: string;
  product_applications: ProductApplication[];
  custom_application?: string;
  product_identity: {
    cas_number: string;
  };
  product: {
    name: string;
    identifiers?: Identifiers;
    produced_by?: string | null;
    slug?: string;
  };
  documents: ProductDocument[];
  last_purchase: LastPurchase;
}

/**
 * Request body for POST /v1/storefronts/{storefront_id_or_slug}/quotes/{id}/items
 * @see QuoteRequestItemCreationArgsSchema
 */
export interface IQuoteRequestItemCreationArgs {
  product_id: string;
  sku_id?: string;
  no_of_units?: string;
  total_quantity?: string;
  packaging_unit_id?: string;
  custom_packaging_type_id?: string;
  custom_packaging_quantity?: string;
  price_per_unit?: string | null;
  currency: CurrencyCode;
  applications: string[]; // UUID strings
  buyer_id?: string;
}

/**
 * Request body for PATCH /v1/storefronts/{storefront_id_or_slug}/quotes/{quote_id}/items/{item_id}
 * @see QuoteRequestItemPatchArgsSchema
 */
export interface IQuoteRequestItemPatchArgs {
  sku_id?: string;
  no_of_units?: string;
  total_quantity?: string;
  packaging_unit_id?: string;
  custom_packaging_type_id?: string;
  custom_packaging_quantity?: string;
  price_per_unit?: string;
  currency?: CurrencyCode;
  applications?: string[];
  custom_application?: string;
  buyer_id?: string;
  requested_sku_id?: string;
  requested_no_of_units?: string;
  requested_total_quantity?: string;
}

export type QuoteStatus =
  | "new"
  | "requested"
  | "responded"
  | "pending"
  | "cancelled"
  | "declined"
  | "accepted"
  | "pending_activation"
  | "expired";

export type QuoteStatusProps = {
  status: QuoteStatus;
  position?: "right" | "left";
  id?: string;
  t?: (s: string) => string;
};

export interface StatusAndId<T> {
  status: T;
  id: string;
}

/**
 * @see ChargeDiscountSchema
 * This has an `id` and is used when retrieving from server.
 *
 * @see ChargeDiscountArgsSchema for the version of this without the `id` that
 * is used for creation.
 */
export interface Fee {
  id: string;
  name: string;
  amount: number;
  currency: CurrencyCode;
}

/**
 * GET /v1/storefronts/{storefront_id_or_slug}/quotes/{id}
 * @see QuoteRequestSchema
 */
export interface QuoteRequest {
  id: string;
  created_at: string; // date
  created_by: string;
  payment_term: IPaymentTerm;
  payment_mode: IPaymentMode;
  delivery_term: IDeliveryTerm;
  shipping_address: IAddress;
  shipping_point_of_contact?: PointOfContact | null;
  billing_address: IAddress;
  required_eta: string;
  modified_at: string;
  modified_by: string;
  number: string;
  items: QuoteRequestItem[];
  seller_documents: TransactionDocument[];
  buyer_documents: TransactionDocument[];
  fees: Fee[];
  total_fees: string;
  valid_to_date: string;
  auto_accept: boolean;
  seller_id: string;
  seller_name: string;
  buyer_id: string;
  buyer_name: string;
  buyer_role: "buyer" | "distributor";
  status: QuoteStatus;
  is_tax_exempt: boolean;
  seller_address: IAddress;
  currency: CurrencyCode;
  payments?: Payment;
  sold_by?: User;
  created_by_company_name?: string;
  is_buyer_activated: boolean;
  discounts: IDiscountCodesSchema[];
  total_discounts: string;
  total_after_discount: string;
  account_manager_name: string;
  customer_service_rep_name: string;
  internal_notes?: LeadNote[];
}

export interface IOrdersAPIResponse {
  data: IPurchaseOrder[];
  pagination: IPagination;
}

/**
 * @see OrderStatus
 */
export type OrderStatus =
  | "new"
  | "in_progress"
  | "accepted"
  | "invoiced"
  | "completed"
  | "declined"
  | "cancelled"
  | "payment_received"
  | "shipped"
  | "completed"
  | "closed"
  | "back_ordered"
  | "pending"
  | "pending_activation"
  | "pending_cancellation";

export type OrderStatusLabel =
  | "Placed"
  | "Accepted"
  | "In Progress"
  | "Invoiced/Payment Received"
  | "Shipped"
  | "Completed"
  | "Closed"
  | "Declined"
  | "Cancelled"
  | "Payment Received"
  | "Back Ordered"
  | "Pending Activation"
  | "Pending Cancellation";

/**
 * Order status action to be sent to the BE to update the Order status
 */
export type OrderStatusAction = Exclude<
  PurchaseOrderAction["purchase_order_action"],
  null
>;

/**
 * @see ShipmentAdviceSchema
 */
export interface IShipmentAdvice {
  id: UUID;
  additional_comments: string | null;
  carrier: string | null;
  carrier_number: string | null;
  estimated_time_of_arrival: string | null;
  estimated_time_of_dispatch: string | null;
  shipment_advice_type: string | null;
  shipment_advice_document: OrderDocument;
}

export type TransactionTypeEnum = "orders" | "sample-requests";

export type LotNumber = {
  id?: UUID;
  item_id: UUID;
  number: string;
  units: number;
};

/**
 * Response body for POST /v1/storefronts/{storefront_id_or_slug}/orders
 * Response body for POST /v1/storefronts/{storefront_id_or_slug}/orders/{id}
 * @see PurchaseOrderSchema
 */
export interface IPurchaseOrder {
  id: string;
  payment_term: IPaymentTerm;
  payment_mode: IPaymentMode;
  delivery_term: IDeliveryTerm;
  billing_address: IAddress;
  shipping_address: IAddress;
  shipping_point_of_contact?: PointOfContact | null;
  required_eta: string; // date-time string, e.g. "2020-08-01T17:32:28Z"
  items: IOrderItem[];
  seller_documents: TransactionDocument[];
  buyer_documents: TransactionDocument[];
  modified_at: string; // date
  modified_by: string;
  created_at: string; // date
  created_by: string;
  fees: Fee[];
  total_fees: string;
  status: OrderStatus;
  number: string;
  seller_name: string | null;
  invoice: InvoiceSchema;
  buyer_id: string;
  seller_id: string;
  buyer_name: string | null;
  buyer_role: "buyer" | "distributor";
  documents: OrderDocument[];
  quote_request_id: string;
  alternative_po_number: string | null;
  seller_address: IAddress;
  shipment_advices?: IShipmentAdvice[];
  is_from_quote: boolean;
  is_tax_exempt: boolean;
  currency: CurrencyCode;
  lot_numbers: LotNumber[];
  payments?: Payment;
  tax_exempt_document?: ProductDocument;
  sold_by?: User;
  created_by_company_name?: string;
  cancellation_reason?: string;
  unread_message_count?: number;
  discounts: IDiscountCodesSchema[];
  total_discounts: string;
  total_after_discount: string;
  account_manager_name: string;
  customer_service_rep_name: string;
  internal_notes?: LeadNote[];
}
export type PurchaseOrderPaginatedOutput = WithPagination<{
  data: IPurchaseOrder[];
}>;

/**
 * Request body for POST /v1/storefronts/{storefront_id_or_slug}/orders/carts
 * @see PurchaseOrderReorderCartArgsSchema
 *
 * Request body for POST /v1/storefronts/{storefront_id_or_slug}/orders/carts/{id}/items
 * @see PurchaseOrderReorderCartItemAddArgsSchema
 *
 * (The two schemas are the same.)
 */
export interface PurchaseOrderReorderCartArgs {
  order_id: string;
  item_id: string;
  no_of_units: number; // decimal
  price_per_unit: number; // decimal
  currency?: CurrencyCode;
  delivery_term_id?: string;
  payment_term_id?: string;
  payment_mode_id?: string;
}

/**
 * Request body for POST /v1/storefronts/{storefront_id_or_slug}/orders/carts/{id}
 * @see PurchaseOrderReorderCreationArgsSchema
 */
export interface PurchaseOrderReorderCreationArgs {
  delivery_term_id: string;
  payment_term_id: string;
  payment_mode_id: string;
  required_eta: string;
}

export type LastPurchase = {
  number_of_units: string;
  price_per_unit: string;
  currency: CurrencyCode;
  sku: ProductSKU;
};

// export function isIOrderItem(item: Object): item is IOrderItem {
//   return item.hasOwnProperty("id") && item.has;
// }

/**
 * This type is used for order items, and also for reorder cart items,
 * since they are the same type as order items.
 *
 * Note: product is nullable in some cases with orders created on V2, not
 * updating the type yet because this is probably a bug.
 *
 * @see PurchaseOrderDetailSchema
 */
export interface IOrderItem {
  id: string;
  total_quantity: string;
  number_of_units: string;
  price_per_unit: string | null;
  currency: CurrencyCode;
  sku: ProductSKU | null;
  product_id: string;
  product_applications: ProductApplication[];
  custom_application?: string;
  documents: ProductDocument[];
  last_purchase: LastPurchase;
  lot_numbers: LotNumber[];
  product: {
    name: string;
    identifiers: Identifiers | null;
  };
  contract_id: string | null;
  total_value: string | null;
}

export interface INotification {
  message: string;
  type: "success" | "info" | "error" | "warning";
  error?: unknown;
}
export type LayoutComponentProps = {
  children: React.ReactNode;
  routes: (RouteType | ExternalRouteType)[];
  omitUserArea?: boolean;
  omitHeader?: boolean;
  fullWidth?: boolean;
  noPadding?: boolean;
  mxWidth?: string;
};

export type INavLayout = "admin" | "portal" | "public";
export interface INavLinkDetails {
  name: string;
  icon?: ({ fill, width, height }: IIconProps) => JSX.Element;
}

export type NavType = "internal" | "external";

export type RouteType = {
  component: React.FunctionComponent;
  path: string;
  browserTitle?: string;
  exact: boolean;
  permission?: Permission | Permission[];
  layout: FC<LayoutComponentProps>;
  children?: ReactNode;
  navType?: NavType;
  metaName?: keyof StorefrontMetaData;
  // Some routes may be used with more than one layout (e.g. portal and public),
  // and may have different nav link details for each layout.
  nav?: Partial<Record<INavLayout, INavLinkDetails>>;
  parentNav?: string;
  internalName?: string;
  layoutProps?: {
    omitUserArea?: boolean;
    omitHeader?: boolean;
    fullWidth?: boolean;
    mxWidth?: string;
    noPadding?: boolean;
  };
};

export type ExternalRouteType = {
  path: string;
  nav: Partial<Record<INavLayout, INavLinkDetails>>;
  navType: NavType;
  metaName?: keyof StorefrontMetaData;
  parentNav?: string;
};

export type IRoutePathKey = "accountPath" | "storePath" | "adminPath";

export type IRoutePaths = Record<IRoutePathKey, string>;

export interface ITag {
  id: string;
  image_url: string | null;
  description?: string;
  title?: string;
  link?: string;
}

type TransactionEventBase = {
  created_at: string;
  quote_message_id: string | null;
  purchase_order_id: string;
  id: string;
  creator_id: string;
  creator: User;
  quote_request_id: string | null;
  order_message_id: string | null;
  sample_request_id: string | null;
  event_type:
    | "status_update"
    | "buyer_edit"
    | "seller_edit"
    | "document_upload"
    | "quote_message"
    | "order_message"
    | "sample_message"
    | "shipment_advice"
    | "document_delete"
    | "purchase_order_number"
    | "payment_link_created"
    | "shipment_advice_changed"
    | "shipment_advice_deleted"
    | "checkout_session_paid"
    | "transaction_split";
  request_json: IMessageRequestJson | null; // This is the request JSON from any quote or order
  // action take by a buyer or seller.
  payment_mode: IPaymentMode | null;
  payment_term: IPaymentTerm | null;
  delivery_term: IDeliveryTerm | null;
  shipping_address: IAddress | null;
  product: Product | null;
  product_sku: ProductSKU | null;
  order_document: OrderDocument;
  cancellation_reason: string;
};

interface PurchaseOrderAction {
  purchase_order_action:
    | "accept"
    | "invoice"
    | "complete"
    | "decline"
    | "cancel"
    | "progress"
    | "ship_order"
    | "pending_cancellation"
    | "resume_order"
    | null;
}

interface QuoteRequestAction {
  quote_request_action:
    | "request"
    | "respond"
    | "accept"
    | "cancel"
    | "decline"
    | null;
}

// First, merge PurchaseOrderAction and QuoteRequestAction exclusively
type POorQuoteExclusive = MergeExclusive<
  PurchaseOrderAction,
  QuoteRequestAction
>;

// Then, merge the result with SampleRequestAction exclusively
export type TransactionEvent = MergeExclusive<
  POorQuoteExclusive,
  SampleRequestAction
> &
  TransactionEventBase;

export type PaginatedTransactionEvents = WithPagination<{
  data: TransactionEvent[];
}>;

export type OrderMessageArgs = {
  message: string;
  message_type:
    | "Documents"
    | "Other"
    | "Packaging"
    | "Price"
    | "Product"
    | "Shipment";
};

/**
 * Success response (201) for POST to:
 * /v1/storefronts/{storefront_id_or_slug}/tenants/{tenant_id_or_slug}/products/{product_id}/images
 * @see ProductImageSchema
 */
export interface IProductImage {
  id: string;
  name: string;
  cdn_url: string;
  size: number;
}

/**
 * Request body for POST to:
 * /v1/{tenant_id_or_slug}/invite
 * @see TenantInviteArgsSchema
 *
 * response body for GET:
 * /v1/storefronts/{storefront_id_or_slug}/tenants/{tenant_id}/invite/{invite_id}
 */
export interface ITenantInviteArgs {
  company_name: string;
  contact_firstname?: string;
  contact_lastname?: string;
  contact_email: string;
  lead_id?: string;
  id?: string;
}

/**
 * @see TenantInviteSchema
 */
export interface ITenantInvite {
  company_name: string;
  contact_firstname: string;
  contact_lastname: string;
  contact_email: string;
  id: string;
  lead: ILeadSchema | null;
  status: string;
}

/**
 * Response type for:
 * GET `/v1/storefronts/{storefront_id_or_slug}/tenants/{tenant_id}/invite
 * @see TenantInvitePaginatedOutputSchema
 */
export interface ITenantInvitePaginatedOutput {
  data: ITenantInvite[];
  pagination: IPagination;
}

/**
 * Request body for POST to:
 * /v1/storefronts/{storefront_id_or_slug}/orders/{order_id}/generated-invoice
 *
 * @see GeneratedInvoiceArgsSchema
 */
export interface IGeneratedInvoiceArgs {
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  ship_via?: string;
  tracking_number?: string;
  additional_charges?: IChargeDiscountArgs[];
  notes?: string;
  is_upload: boolean;
}

/**
 * @see InvoiceSchema
 */
export type InvoiceSchema = {
  id: UUID;
  purchase_order_id: UUID;
  document: OrderDocument;
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  ship_via: string | null;
  tracking_number: string | null;
  additional_charges: Fee[];
  notes: string | null;
};

/**
 * @see ChargeDiscountArgsSchema
 */
interface IChargeDiscountArgs {
  name: string;
  amount: string;
  currency: CurrencyCode;
}

/**
 * @see UserSchema
 */
interface UserSchema {
  id: number;
  first_name: string;
  last_name: string;
  user_name: string;
  email: string;
  role: string;
  referral: string;
  phone_number: string;
  created_at: string;
  modified_at: string;
}

/**
 * Response body for POST:
 * /v1/storefronts/{storefront_id_or_slug}/invite/{invite_id}
 * @see PasswordResetTokenSchema
 */
export interface PasswordResetTokenSchema {
  id: string;
  user: UserSchema;
  expire_at: string;
}

/**
 * Represents a price tier for a given product + SKU + shipping_address + buyer
 * + seller + etc.
 *
 * `minimum_sku_quantity` refers to a minimum number of SKU units.
 *
 * `minimum_uom_quantity` refers to a minimum number of "unit of measure"
 * quantity, which is the value calculated by multiplying the number of SKU
 * units and the quantity of each SKU.
 *
 * For example, if the SKU is "Boxes (5 LBs)" and you have 5
 * `minimum_sku_quantity` then you have 25 LBs of `minimum_uom_quantity` (5 x 5).
 *
 * @see PriceTierSchema
 */
export interface IPriceTier {
  id: string; // UUID
  seller_id: string; // UUID
  buyer_id: string; // UUID
  destination: IAddress;
  product_sku: ProductSKU;
  delivery_term: IDeliveryTerm;
  payment_term: IPaymentTerm;
  payment_mode: IPaymentMode;
  minimum_uom_quantity: string; // decimal, e.g. 1234.56
  minimum_sku_quantity: string; // decimal, e.g. 1234.56
  price_per_uom: string; // decimal, e.g. 1234.56
  currency: CurrencyCode;
  valid_from_date: string; // datetime, e.g. '2021-08-01T12:34:56
  valid_to_date: string; // satetime, e.g. '2021-08-01T12:34:56
}

/**
 * Response body for:
 * GET /v1/price-tiers
 * @see PriceTierPaginatedOutputSchema
 */
export type IPriceTierPaginatedOutput = WithPagination<{
  data: IPriceTier[];
}>;

/**
 * Request body for:
 * POST /v1/price-tiers
 * @see PriceTierCreateSchema
 *
 * If you include quote_request_detail_id or purchase_order_detail_id then
 * there's no reason to include destination_id, product_sku_id,
 * delivery_term_id, payment_term_id, and payment_mode_id since they would
 * have no effect. In that case those values are ignored and are taken from the
 * quote request or purchase order.
 */
export interface IPriceTierCreate {
  price_tiers: {
    seller_id: string; // UUID
    buyer_id: string; // UUID
    price_per_uom: string; // decimal, e.g. 1234.56
    minimum_uom_quantity?: string; // decimal, e.g. 1234.56
    minimum_sku_quantity?: string; // decimal, e.g. 1234.56
    currency?: CurrencyCode;
    valid_from_date?: string; // datetime, e.g. '2021-08-01T12:34:56
    valid_to_date?: string; // satetime, e.g. '2021-08-01T12:34:56
    quote_request_detail_id?: string; // UUID
    purchase_order_detail_id?: string; // UUID
    //
    destination_id?: string; // UUID
    product_sku_id?: string; // UUID
    delivery_term_id?: string; // UUID
    payment_term_id?: string; // UUID
  }[];
}

/**
 * Request body for:
 * PATCH /v1/storefronts/{storefront_id}/price-tiers/{price_tier_id}
 * @see PriceTierPatchSchema
 */
export interface IPriceTierPatch {
  delivery_term_id?: string; // UUID
  payment_term_id?: string; // UUID
  payment_mode_id?: string; // UUID
  valid_from_date?: string; // datetime, e.g. '2021-08-01T12:34:56
  valid_to_date?: string; // satetime, e.g. '2021-08-01T12:34:56
  minimum_uom_quantity?: string; // decimal, e.g. 1234.56
  minimum_sku_quantity?: string; // decimal, e.g. 1234.56
  price_per_uom?: number; // decimal, e.g. 1234.56
  currency?: CurrencyCode;
  sku_id?: string;
}

/**
 * Response body for GET /v1/storefronts/{storefront_id_or_slug}/quotes/{quote_id}/items/{item_id}/price-tiers
 * Response body for GET /v1/storefronts/{storefront_id_or_slug}/orders/{order_id}/items/{item_id}/price-tiers
 * @see QuoteRequestPriceTierSchema
 */
export interface IQuoteRequestPriceTier {
  price_per_uom: string;
  price_tiers: IPriceTier[];
  currency: CurrencyCode;
}

/**
 * Response body for GET /v1/price-tiers/grouped
 *
 * @see GroupedPriceTierSchema
 */
export interface IGroupedPriceTier {
  seller_id: string;
  buyer_id: string;
  product: {
    id: string;
    name: string;
  };
  destination: IAddress;
  delivery_term: IDeliveryTerm;
  payment_term: IPaymentTerm;
  payment_mode: IPaymentMode;
  currency: CurrencyCode;
  valid_from_date: string; // datetime
  valid_to_date: string; // datetime
  terms: IGroupedPriceTierSingleTier[];
}

export interface IGroupedPriceTierSingleTier {
  product_sku: ProductSKU;
  price_per_uom: string; // decimal
  minimum_uom_quantity: string; // decimal
  minimum_sku_quantity: string; // decimal
  tier_id: string;
}

/**
 * Response body for GET /v1/last-purchase
 */
export interface LastPurchaseResponse {
  currency: CurrencyCode;
  number_of_units: string;
  price_per_unit: string;
  sku: ProductSKU;
}

export interface AccountSubscriber {
  id: UUID;
  firstname: string;
  lastname: string;
  email_address: string;
}
/**
 * Response body of GET, POST, and PATCH to:
 * /v1/storefronts/{storefront_id_or_slug}/tenants/{seller_id_or_slug}/customers/{buyer_id_or_slug}/settings
 *
 * @see TenantCustomerSettingsSchema
 */
export interface ITenantCustomerSettings {
  account_manager?: User;
  customer_service_rep?: User;
  default_delivery_term?: IDeliveryTerm;
  default_payment_mode?: IPaymentMode;
  default_payment_term?: IPaymentTerm;
  preferred_currency?: CurrencyCode;
  is_tax_exempt?: boolean;
  tax_exempt_document?: ProductDocument;
  subscribers?: AccountSubscriber[];
  notification_preferences?: {
    quote_notifications?: boolean;
    order_notifications?: boolean;
    sample_request_notifications?: boolean;
  };
}

/**
 * Request body for POST and PATCH to:
 * /v1/storefronts/{storefront_id_or_slug}/tenants/{seller_id_or_slug}/customers/{buyer_id_or_slug}/settings
 *
 * @see TenantCustomerSettingsCreationSchema
 */
export interface ITenantCustomerSettingsCreation {
  account_manager_id?: string; // UUID
  customer_service_rep_id?: string; // UUID
  default_delivery_term_id?: string; // UUID
  default_payment_mode_id?: string; // UUID
  default_payment_term_id?: string; // UUID
  preferred_currency?: CurrencyCode;
  is_tax_exempt?: boolean;
}

/**
 * @see SampleRequestCreationArgsSchema
 */
export type SampleRequestCreationArgs = {
  product_id: string;
  purpose: string;
  total_quantity: string;
  total_quantity_packaging_unit_id: string;
  sample_quantity?: string;
  sample_quantity_packaging_unit_id?: string;
  applications?: string[];
  custom_application?: string;
  sku_id: string;
  no_of_units: string;
};

export interface ISampleRequestItemPatchArgs {
  item_id: string;
  sku_id: string;
  no_of_units: string;
  total_quantity: string;
  packaging_unit_id?: string;
  custom_packaging_type_id?: string;
  custom_packaging_quantity?: string;
  requested_sku_id?: string;
  requested_no_of_units?: string;
  requested_total_quantity?: string;
}

/**
 * named as such so as not to conflict with SampleRequestItem component
 */
export type SampleRequestIndividualItem = {
  packaging_unit: Pick<IPackagingUnit, "name" | "id"> | null;
  product: {
    name: string;
    identifiers?: Identifiers;
  };
  purpose: string;
  product_id: string;
  total_quantity: string;
  sample_quantity_packaging_unit: IPackagingUnit | null;
  total_quantity_packaging_unit: IPackagingUnit | null;
  sample_quantity: string;
  id: string;
  applications?: ProductApplication[];
  custom_application?: string;
  price_per_unit?: string | null;
  currency: CurrencyCode;
  number_of_units: string;
};

export type SampleRequestStageOne = {
  buyer_first_name: null;
  buyer_address: null;
  buyer_id: null;
  status: "new";
  buyer_last_name: null;
  buyer_email: null;
  buyer_company_name: null;
  modified_at: string;
  seller_id: string;
  buyer_phone: null;
  id: string;
  items: SampleRequestIndividualItem[];
};

export type SampleRequestStatusUnion =
  | "requested"
  | "responded"
  | "accepted"
  | "new"
  | "forwarded"
  | "converted"
  | "in_progress"
  | "shipped"
  | "pending"
  | "completed"
  | "Added as User"
  | "Added as Address"
  | "rejected"
  | "pending_activation"
  | "blocked"
  | "sent_to_distributor"
  | "rejected by distributor"
  | "accepted by distributor"
  | "followed_externally";

/**
 * Request body to /v1/storefronts/{storefront_id_or_slug}/sample-requests/{id}
 * This is used once the sample request is already in @See SampleRequestStageOne
 */
export type PatchSampleRequest = Omit<IAddressInfo, "postal_code"> & {
  buyer_first_name: string;
  buyer_last_name: string;
  buyer_email: string;
  buyer_phone: string;
  buyer_company_name: string;
  zip_code: string;
  country: string;
  county?: string;
  state: string;
  requested_documents: string[];
  decision_timeline: string;
  is_decision_maker: boolean;
  billing_address?: IAddressInfo & { county?: string };
  captcha_token?: string;
  recaptcha_key?: string;
};

export type SampleRequestStageTwo = {
  buyer_first_name: string;
  buyer_company_name: string;
  buyer_address: IAddress;
  buyer_email: string;
  buyer_id: null;
  buyer_phone: string;
  status: SampleRequestStatusUnion;
  assignee: LeadAssignee | null;
  assignment_date: string | null;
  items: SampleRequestIndividualItem[];
  buyer_last_name: string;
  modified_at: string;
  id: UUID;
  number: string; // human readable id
  seller_id: string;
  created_at: string; // Date
  is_decision_maker: boolean;
  decision_timeline: string;
  requested_documents: string[];
  is_existing_customer: boolean;
  company_name: string;
  customer_id: string;
  parent_customer_id: string;
  shipping_address?: IAddress;
  billing_address?: IAddress;
  required_eta?: string;
  internal_notes: LeadNote[];
  rejection_note?: RejectionNote;
  region?: string;
  subscribers: LeadAssignee[];
  subscribers_updated_on?: string | null;
  subscriber_assignment_type?: "new" | "updated";
  lead_intel?: LeadIntel | null;
};

/**
 * @see SampleRequestSchema where this enum is used.
 */
export type SampleRequestStatus =
  | "new"
  | "requested"
  | "responded"
  | "price_received"
  | "accepted"
  | "rejected"
  | "cancelled"
  | "completed"
  | "in_progress"
  | "invoiced"
  | "payment_received"
  | "shipped"
  | "pending"
  | "no_request"
  | "pending_activation"
  | "pending_cancellation";

export type SampleStatusLabel =
  | "New"
  | "Accepted"
  | "In Progress"
  | "Invoiced"
  | "Payment Received"
  | "Shipped"
  | "Requested"
  | "Responded"
  | "Price Received"
  | "Cancelled"
  | "Rejected"
  | "No Request"
  | "Pending Activation"
  | "Pending Cancellation"
  | "Completed";

interface SampleRequestAction {
  sample_request_action:
    | "accept"
    | "complete"
    | "decline"
    | "cancel"
    | "reject"
    | "progress"
    | "ship_sample"
    | "pending_cancellation"
    | "resume_sample"
    | "respond"
    | "invoice"
    | null;
}

/**
 * Sample status action to be sent to the BE to update the Sample status
 */
export type SampleStatusAction = Exclude<
  SampleRequestAction["sample_request_action"],
  null
>;

/**
 * Response body for
 * GET /v1/storefronts/{storefront_id}/sample-requests/{sample_request_id}
 *
 * @see SampleRequestDetailSchema
 */
export interface SampleRequestDetail {
  id: string;
  total_quantity: string;
  sample_quantity: string;
  purpose: string;
  sku: ProductSKU;
  // not sure if these units are part of PIM
  total_quantity_packaging_unit: IPackagingUnit | null;
  sample_quantity_packaging_unit: IPackagingUnit | null;
  product_id: string;
  applications: ProductApplication[];
  custom_application?: string;
  documents: ProductDocument[];
  product: {
    name: string;
    identifiers?: Identifiers;
    // The SDS and TDS document URLs were put here in haste to meet a deadline on
    // Jan 6, 2022. To match what we do with Orders, there should be an array
    // like `documents: ProductDocument[]` on the SampleRequestDetail object.
    safety_datasheet?: string; // SDS (ProductDocument.signed_url)
    technical_datasheet?: string; // TDS (ProductDocument.signed_url)
  };
  no_of_units: string;
  price_per_unit?: string | null;
  currency: CurrencyCode;
  lot_numbers?: LotNumber[];
  enable_payments?: boolean;
}

export type RequestedBySchema = {
  contact_name: string;
  email_address: string;
  company_name: string;
  phone_number: string;
};

/**
 * @see OrderDocumentSchema
 */
type SampleRequestDocument = SampleDocument;

/**
 * @see SampleRequestSchema
 */
export interface SampleRequest extends Omit<IAddressInfo, "postal_code"> {
  id: string;
  modified_at: string;
  created_at: string;
  created_by: string;
  modified_by: string;
  buyer_first_name: string;
  buyer_last_name: string;
  buyer_company_name: string;
  county: string;
  zip_code: string;
  items: SampleRequestDetail[];
  seller_documents: TransactionDocument[];
  buyer_documents: TransactionDocument[];
  invoice: InvoiceSchema;
  seller_id: string;
  buyer_id: string;
  status: SampleRequestStatus;
  shipping_address?: IAddress;
  shipping_point_of_contact?: PointOfContact | null;
  seller_address?: IAddress;
  buyer_address?: IAddress;
  buyer_role: "buyer" | "distributor" | undefined;
  requested_by?: RequestedBySchema;
  requested_by_hq_address?: IAddress;
  number: string;
  decision_timeline: string;
  is_decision_maker: boolean;
  requested_documents: string[];
  shipment_advices?: IShipmentAdvice[];
  documents: SampleRequestDocument[];
  sold_by: User;
  company_name: string;
  created_by_company_name: string;
  is_buyer_activated: boolean;
  cancellation_reason?: string;
  unread_message_count: number;
  account_manager_name: string;
  customer_service_rep_name: string;
  enable_payments?: boolean;
  payment_term: IPaymentTerm;
  payment_method: IPaymentMode;
  delivery_term: IDeliveryTerm;
  required_eta?: string;
  valid_to_date?: string;
  fees?: Fee[];
  total_fees?: string;
  currency: CurrencyCode;
  payments?: Payment;
  discounts?: IDiscountCodesSchema[];
  total_discounts?: string;
  total_after_discount?: string;
  is_tax_exempt?: boolean;
  internal_notes?: LeadNote[];
  buyer_status: SampleRequestStatus;
  seller_status: SampleRequestStatus;
  alternative_po_number: string | null;
}

/**
 * Response body for:
 * GET /v1/storefronts/{storefront_id}/sample-requests
 *
 * @see SampleRequestPaginatedOutputSchema
 */
export type SampleRequestPaginatedOutput = WithPagination<{
  data: SampleRequest[];
}>;

/**
 * Request body for:
 * POST /v1/storefronts/{storefront_id}/sample-requests/{sample_request_id}
 *
 * Used to submit the logged in buyer sample request cart.
 *
 * @see SampleRequestSubmitLoginArgsSchema
 */
export interface SampleRequestSubmitLoginArgs {
  is_decision_maker: boolean;
  decision_timeline: string;
  shipping_address_id: string;
  shipping_point_of_contact_id: string;
  requested_documents?: string[];
}

export interface ICountry {
  alpha_2: Alpha2;
  country_code: string;
  name: string;
  official_name: string;
}
export interface IState {
  code: string;
  country_code: string;
  display_name: string;
  name: string;
  parent_code: string;
  type: string;
}

export type ICountriesAPIResponse = ICountry[];
export type IStatesAPIResponse = IState[];

/**
 * An array of these records are returned from:
 * GET /v1/storefronts/{id}/tenant/defaults/delivery-terms/{id}
 * GET /v1/storefronts/{id}/tenant/defaults/payment-terms/{id}
 * GET /v1/storefronts/{id}/tenant/defaults/payment-modes/{id}
 */
export interface ITenantNameAndId {
  id: string;
  name: string;
}

/**
 * @see MyProductSchema
 */
export type MyProductsProduct = {
  currency: CurrencyCode;
  modified_at: string | null; // Date
  number_of_units: number;
  price_per_unit: number | null;
  product: {
    name: string;
    id: UUID;
    identifiers: Identifiers;
    status: ProductStatusType;
  };
  product_id: UUID;
  transacted_product_id: UUID | null;
  cas_number: string;
  chemical_name: string;
  ec_number: string;
  inchi_key: string;
  inci_name: string | null;
  molecular_formula: string | null;
  synonyms: string | null;
  product_price_tiers: IPriceTier[];
  total_quantity: number;
  ytd_quantity: number | null;
  order: IPurchaseOrder | null;
};

/**
 * response body for `/v1/storefronts/{id}/my-products.
 * This is not a typo in the schema name, the endpoint is really plural and the
 * schema singular.
 * @see MyProductPaginatedOutputSchema
 */
export type MyProductsPaginatedOutput = WithPagination<{
  data: MyProductsProduct[];
}>;

/**
 * Supposedly only one can exist at once but I have never personally seen the
 * `order_cart` exist.
 * @see UnifiedCartGetSchema
 * @see PurchaseOrderCartSchema this is an alias for the stock
 * purchase order schema.
 */
export type StorefrontUnifiedCart = {
  quote?: IQuoteRequest;
  order_cart?: IPurchaseOrder;
};
export interface IEmailNotificationTemplate {
  subject: string | undefined;
  header: string | undefined;
  content: string | undefined;
  footer: string | undefined;
  recipients: string | undefined;
  template: string;
}

export interface IEmailTemplateEditorData {
  label: string;
  language: SupportedLanguage;
  key: EmailTemplateKey;
  templateData?: IStorefrontEmailTemplate;
}

export type SupportedLanguage = string;

export type Language = {
  alpha_2: SupportedLanguage;
  alpha_3: string;
  name: string;
  is_deletable?: boolean;
};

export type IStorefrontEmailTemplateBodyPreview = string;

/**
 * Response body for GET to:
 * /v1/storefronts/{id}/email-templates/{template_id}
 * /v1/email-templates/{template_id}
 */
export interface IStorefrontEmailTemplate {
  id: string;
  template_key: EmailTemplateKey;
  language: SupportedLanguage;
  template_body: string;
  template_subject: string;
  template_file: string;
  // The first string is `{{variable_name}}` and the second is the description.
  template_variables: [string, string][];
  email_body: string | null;
  customer_email_text: string;
  customer_email_thanks: string;
  custom_header: string;
  custom_footer: string;
  subject?: string;
  recipients: string;
}

/**
 * Request body for POST to:
 * /v1/storefronts/{storefront_id_or_slug}/email-templates
 *
 * @see StorefrontCustomTemplateCreateSchema
 */
export interface IStorefrontCustomTemplateCreate {
  template_key: string;
  template_body: string;
  template_subject: string;
  language: SupportedLanguage;
  custom_header?: string;
  custom_footer?: string;
  email_body?: string | null;
}

/**
 * Request body for PATCH to:
 * /v1/storefronts/{storefront_id_or_slug}/email-templates/{template_id_or_slug}
 *
 * @see StorefrontCustomTemplatePatchSchema
 */
export interface IStorefrontCustomTemplatePatch {
  template_body?: string; // Usually you don't want to touch this in a PATCH.
  template_subject?: string;
  language?: SupportedLanguage;
  custom_header?: string;
  custom_footer?: string;
  email_body?: string | null;
}

export type EmailTemplateKey =
  | "tenant_activation"
  | "user_activation"
  | "distributor_activation"
  | "forward_lead"
  | "to_sales_team_from_agilis_site_agilis_site_registration"
  | "to_sales_team_from_agilis_site_agilis_site_demo_request"
  | "to_sales_team_from_agilis_site_agilis_contact_request"
  | "notify_seller_quote_request"
  | "notify_seller_ul_prospector_connector"
  | "notify_seller_contact_form"
  | "notify_seller_registration"
  | "notify_customer_agilis_site_registration"
  | "notify_customer_registration"
  | "notify_customer_agilis_contact_request"
  | "notify_customer_contact_form"
  | "notify_customer_agilis_site_demo_request"
  | "notify_customer_quote_request"
  | "notify_customer_ul_prospector_connector"
  | "notify_customer_lead_rejected_agilis_site_registration"
  | "notify_customer_lead_rejected_registration"
  | "notify_customer_lead_rejected_agilis_contact_request"
  | "notify_customer_lead_rejected_contact_form"
  | "notify_customer_lead_rejected_agilis_site_demo_request"
  | "notify_customer_lead_rejected_quote_request"
  | "notify_customer_lead_rejected_ul_prospector_connector"
  | "transaction_quote_no_request"
  | "transaction_quote_new"
  | "transaction_quote_requested"
  | "transaction_quote_responded"
  | "transaction_quote_accepted"
  | "transaction_quote_cancelled"
  | "transaction_quote_declined"
  | "transaction_order_no_order"
  | "transaction_order_new"
  | "transaction_order_accepted"
  | "transaction_order_delivered"
  | "transaction_order_invoiced"
  | "transaction_order_closed"
  | "transaction_order_declined"
  | "transaction_order_cancelled"
  | "forgot_username"
  | "forgot_password";

// Use this type to pass `methodsOfUseForm` (the return value of `useForm`) as
// a prop.
export type MethodsOfUseForm = Pick<
  ReturnType<typeof useForm>,
  keyof UseFormMethods
>;

export type CreatedAcceptedOrderItem = {
  product_id: UUID;
  sku_id: UUID;
  sku: TenantSkuCreationArgs | UUID;
  price_per_uom: number; // This may not be consistent, should confirm.
  no_of_units: number; // Can this be decimal?,
  applications: string; // only one is now possible.
};

/**
 * POST body for /v1/storefronts/{id}/transactions
 * TODO: update with schema reference when it exists
 */
export type CreateAcceptedOrder = {
  transaction_state: "order_accepted"; // in the future will be enum.
  data: {
    buyer_id: UUID;
    tenant_id: UUID;
    order_placed_via: "Phone" | "Email" | "Text" | "Fax";
    shipping_address_id: UUID;
    delivery_term_id: UUID; // Potentially change to shipping_term_id
    payment_term_id: UUID;
    requested_eta: Date;
    payment_mode_id: UUID; // technically a payment method
    items: CreatedAcceptedOrderItem[];
  };
};

export type SelectionWizardFilters = {
  filter_values: string[];
  filter_key: string;
  filter_question: string;
};

/**
 * response body for /v1/storefronts/{id}/selections
 */
export type SelectionWizardResponse = {
  filter_query_parameters: string;
  show_submit: boolean;
  is_final: boolean;
  filters: SelectionWizardFilters[];
};

// #/components/schemas/StorefrontPurchaseOrderReportFieldSchema
export type StorefrontPurchaseOrderReportField = {
  field_name: string;
  display_name: string;
  field_type: string | number | Array<any>;
};

export type DownloadLinkOptions = {
  name: string;
  type: string; // example: "text/csv"
  data: string;
};

/**
 * POST /v1/storefronts/{id}/support
 */
export interface ISupportRequest {
  description: string;
  name: string;
  email: string;
  phone_number: string;
  company_name: string;
  captcha_token?: string;
  recaptcha_key?: string;
}

/**
 * backend schema: StorefrontFilterSettingsSchema
 */
export interface StorefrontFilterSetting {
  filter_type: FilterType;
  is_active: boolean;
  is_visible: boolean;
  is_searchable?: boolean | undefined;
  has_product: boolean;
  filter_labels: FilterLabel[];
}
export interface NotificationObject {
  ids: UUID[];
  total: number;
}

export interface InAppNotificationsResponse {
  leads: {
    contact_us: NotificationObject;
    sample_requests: NotificationObject;
    registrations: NotificationObject;
    quote_requests: NotificationObject;
    total: number;
  };
  samples: NotificationObject;
  quotes: NotificationObject;
  orders: NotificationObject;
}

export type DataMutate<T> = (
  data?: T | Promise<T> | mutateCallback<T>,
  shouldRevalidate?: boolean
) => Promise<T | undefined>;

/**
 * GET & PATCH
 * controls whether product resources such as links and documents are visible/downloadable
 * outide of login at the storefront level. Takes precedence over the product
 * level settings for the same resources.
 */
export type StorefrontProductResourcesVisibility = {
  is_sds_visible: boolean;
  is_tds_visible: boolean;
  is_misc_visible: boolean;
  is_links_visible: boolean;
};

export type FontType = "large" | "medium" | "regular" | "small";

export type Payment = {
  /**
   * (url of most recent payment_link),
   */
  payment_link_url: URL;
  /**
   * amount (in USD) of charge in payment link
   */
  payment_link_amount: string;
  /**
   * amount (in USD) of expected stripe fee,
   */
  estimated_stripe_fee: string;
  /**
   * amount (in USD) of expected agilis fee
   */
  estimated_agilis_fee: string;
  /**
   * net amount (in USD),
   */
  estimated_net: string;
  payment_status:
    | "payment_link_created"
    | "charge_failed"
    | "charge_succeeded"
    | "payment_complete";
  payment_amount?: string; // amount (in USD) of successful payment,
  stripe_fee?: string; // amount (in USD) of collected stripe fee
  agilis_fee?: string; // amount (in USD) of collected agilis fee
  payment_net?: string; // amount (in USD) of net payment to seller,
  receipt_url?: string; // url from stripe with buyer receipt
};

/**
 * @see StripePaymentLinkSchema
 */
export type StripePaymentLinkSchema = {
  checkout_sessions: unknown;
  created_at: string;
  modified_at: string;
  application_fee_amount: UUID;
  id: UUID;
  active: boolean;
  modified_by: string;
  payment_link_id: string;
  url: string;
  created_by: string;
  order_id: UUID;
};

export type PaymentFeePercentages = {
  agilis_application_fee_base: string;

  agilis_application_fee_percent: string;

  stripe_fee_base: string;

  stripe_fee_percent: string;
};

export type DecisionTimeline =
  | "< 3 months"
  | "3 - 6 months"
  | "6 - 9 months"
  | "1+ years"
  | "Not sure";

export type TransactionPlacedVia = "Email" | "Fax" | "Phone" | "Text";

export type RequestableDocuments = "SDS" | "TDS" | "COA";

/**
 * Potential post body for /v1/transactions
 */
export type CreateSampleRequest = {
  transaction_state: "sample_accepted" | "sample_requested";
  data: {
    buyer_id: UUID;
    tenant_id: UUID;
    sample_placed_via: TransactionPlacedVia;
    shipping_address_id: UUID;
    shipping_point_of_contact_id: UUID | null;
    is_decision_maker: boolean;
    decision_timeline: DecisionTimeline;
    requested_documents?: RequestableDocuments[];
    items: {
      product_id: UUID;
      purpose: string;
      no_of_units: string;
      total_quantity: string;
      total_quantity_packaging_unit_id?: UUID;
      price_per_unit?: string | null;
      sku: {
        id: UUID;
      };
    }[];
    buyer_user_id: string | null;
    payment_term_id?: UUID;
    payment_mode_id?: UUID;
    delivery_term_id?: UUID;
    required_eta?: string;
    valid_to_date?: string;
    fees_to_add?: Fee[];
  };
};

export type TaxExemptionDocument = {
  kind: "customer_tax_exemption";
  name: string;
  size: number;
  signed_url: string;
  id: string;
};

/**
 * @see DashboardSummarySchema
 */
export type DashboardSummary = {
  total_active_products: number;
  total_active_assets: number;
  total_active_customers: number;
  total_active_users: number;
  new_change_requests?: number;
  total_change_requests?: number;
  average_active_completion_score: number;
  average_published_completion_score: number;
  average_active_mandatory_completion_score: number;
  average_published_mandatory_completion_score: number;
};

export type DashboardUpdate = {
  type:
    | "product"
    | "list"
    | "assets"
    | "attribute"
    | "collection"
    | "group"
    | "template"
    | "customer_template"
    | "supplier_template"
    | "distributor_template";
  last_modified_date: string;
  type_id: string;
  modified_by: {
    name: string;
    role: string;
  };
};

export type ProductDashboardUpdateData = {
  update_type: "product" | "change_request";
  product_id: string;
  name: string;
  updated_properties: string[];
  status: ProductStatusType;
  // Optional change-request fields
  request_title?: string;
  request_details?: string;
  requesting_user?: { name: string; role: string };
  submitted_at?: string;
  change_request_id?: string;
};

export type ListDashboardUpdateData = {
  update_type: "list";
  name: string;
  status: "archived" | "created" | "updated";
  display_name: string;
  values_added?: string[];
  values_removed?: string[];
};

export type TemplateDashboardUpdateData = {
  updated_type: "template";
  name: string;
  status: "created" | "updated";
  updated: string[];
};

export type AssetDashboardUpdateData = {
  updated_type: "asset";
  name: string;
  file_name: string;
  asset_type: string;
  asset_category: string;
  language: SupportedLanguage;
  status: "created" | "updated" | "deleted";
};

export type GroupDashboardUpdateData = {
  updated_type: "group";
  name: string;
  display_name: string;
  attributes_added?: string[];
  attributes_removed?: string[];
  status: "created" | "updated" | "archived";
};

export type CollectionDashboardUpdateData = {
  updated_type: "collection";
  name: string;
  display_name: string;
  attributes_added?: string[];
  attributes_removed?: string[];
  status: "created" | "updated" | "archived";
};

export type AttributeDashboardUpdateData = {
  updated_type: "attribute";
  name: string;
  display_name: string;
  description: string;
  type: AttributeDataType;
  status: "created" | "updated" | "archived";
};

export type DashboardProductUpdate = DashboardUpdate & {
  update_data: ProductDashboardUpdateData;
};

export type DashboardOtherUpdate = DashboardUpdate & {
  update_data:
    | ListDashboardUpdateData
    | TemplateDashboardUpdateData
    | AssetDashboardUpdateData
    | GroupDashboardUpdateData
    | CollectionDashboardUpdateData
    | AttributeDashboardUpdateData;
};

export type StorefrontSummarySchema = {
  id: UUID;
  header: string;
  slug: string;
};

export type ProductFiltersResponse = {
  [key: string]: string;
};

export type ProductSelectorCSVDocument = {
  name: string;
  size: number;
  signed_url: string;
  id: string;
};
export type StorefrontCustomizableFormSchema = {
  is_active: boolean;
  csv_filename: string;
  custom_fields: {
    name: string;
    is_required: boolean;
    choices: {
      choice_name: string;
    }[];
  }[];
  dependent_choices?: {
    choice_name: string;
    choices: {
      choice_name: string;
      choices?: {
        choice_name: string;
      }[];
    }[];
  }[];
};

export type TeamProductSchema = {
  permission: TeamPermissionsEnum;
  product: AssignedProductSchema;
  product_id: UUID;
  team: TeamSummary;
  team_id: UUID;
  visibility: "global" | "team_specific";
} & TeamProductPermissionSchema;

export type TeamProductPermissionSchema = {
  can_view: boolean;
  can_edit: boolean;
  can_publish: boolean;
};

export type TeamPermissionsEnum =
  | "no_permission"
  | "can_view"
  | "can_edit"
  | "can_publish";

export type TeamSummary = {
  team_id: UUID;
  team_name: string;
  status: "active" | "archived";
  number_of_users: number;
  number_of_products: number;
  tenant_id: UUID;
};

export type TeamListResponseSchema = WithPagination<{
  data: TeamProductSchema[];
}>;

export type AssignedUserSchema = {
  user_id: UUID;
  firstname: string;
  lastname: string;
  role: string | null; // Do we have a type for this?
  is_active: boolean;
};

export type TeamDetailsSchema = {
  team_id: UUID;
  team_name: string;
  number_of_products: number;
  number_of_users: number;
  users: Array<AssignedUserSchema>;
  team_products: Array<TeamProductSchema>;
  status: TeamSummary["status"];
  tenant_id: UUID;
};
/**
 * @see TeamCreateSchema
 */
export type TeamCreateSchema = {
  team_name: string;
  user_ids: UUID[];
  product_ids: UUID[];
};

export type AssignedProductSchema = {
  product_name: string;
  status: ProductStatusType;
  product_number: string; // human readable ID
  product_schema: {
    display_name: string | null;
    template_id: string;
    template_name: string;
  };
};

export type TeamCreateResponseSchema = TeamSummary;

export type UpdateTeamSchema = {
  team_name: string;
  user_ids_to_add: UUID[];
  user_ids_to_remove: UUID[];
  product_ids_to_add: UUID[];
  product_ids_to_remove: UUID[];
};

export type TranslationsSchema = {
  language: SupportedLanguage;
  display_name: string;
  description: string;
};

export type SampleRequestNextStatusSchema = {
  transitions: SampleRequestStatus[];
};

export type UserPaginatedOutputSchema = WithPagination<{ data: User[] }>;

export interface ISampleRequest extends Omit<IAddressInfo, "postal_code"> {
  id: string;
  modified_at: string;
  created_at: string;
  created_by: string;
  modified_by: string;
  buyer_first_name: string;
  buyer_last_name: string;
  buyer_company_name: string;
  county: string;
  zip_code: string;
  items: SampleRequestDetail[];
  seller_id: string;
  buyer_id: string;
  status: SampleRequestStatus;
  shipping_address?: IAddress;
  seller_address?: IAddress;
  number: string;
  decision_timeline: string;
  is_decision_maker: boolean;
  requested_documents: string[];
  shipment_advice?: IShipmentAdvice;
  documents: SampleRequestDocument[];
}

export type ProductUploadSchema = {
  id: string;
  upload_number: string;
  template_name: string;
  status: string;
  created_at: string;
  created_by?: string | null;
  upload_log_url?: string | null;
};

export type StorefrontEmailReminderKind =
  | "accept_price"
  | "accept_order"
  | "contact_us"
  | "quote_request"
  | "quote_request_lead"
  | "register"
  | "respond_sample"
  | "sample_request";

export type StorefrontEmailReminderNumberRange =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 21
  | 28
  | 35
  | 42
  | 49
  | 56
  | 63
  | 70
  | 77
  | 84;

export type StorefrontEmailReminderSchema = {
  first_reminder: StorefrontEmailReminderNumberRange | null;
  second_reminder: StorefrontEmailReminderNumberRange | null;
  reminder_type: StorefrontEmailReminderKind;
};

export type StorefrontEmailRemindersSchema = {
  reminder_data: StorefrontEmailReminderSchema[];
};

export type SuggestedPromptGroup = {
  label: string;
  prompts: string[];
};

export type StorefrontChatbotConfiguration = {
  avatar: string;
  id: string;
  intro_message: string;
  inside_login_disclaimer: string;
  kind: string;
  name: string;
  is_enabled: boolean;
  suggested_prompts?: SuggestedPromptGroup[];
};

// Define the shape of our list state
export type ListState = {
  tabIndex: number;
  offset: number;
  perPage: number;
  selectedStatusList: string[];
  searchQuery: string;
};

// Define the shape of our location state
export type LocationState = {
  listState?: ListState;
};

export type TemplateLocationState = {
  searchQuery?: string;
  templateType?: TemplateType;
};

export type AttributeListLocationState = {
  searchQuery?: string;
  offset?: number;
  perPage?: number;
  attributeTypeFilters?: string[];
};
export type AccountManagerContactInfo = {
  contact_last_name: string;
  contact_first_name: string;
  contact_name: string;
  country: string;
  id: string;
  phone_number: string;
  email_address: string;
  crm_id?: string | null;
  is_active: boolean;
};

export type AnalyticsProp = {
  analyticsName: string;
  eventType: string;
  link?: string;
  objectId: string;
};

export interface IFeesAndChargesSchema {
  id: string;
  name: string;
  amount: string;
  description: string;
  has_transactions?: boolean;
  currency?: CurrencyCode;
}

export interface IDiscountCodesSchema {
  id: string;
  discount_code: string;
  type: string;
  amount: number;
  valid_from: string;
  expires_on: string;
  valid?: boolean;
  message?: string;
}

export type ProductInfo = {
  internalLink: string;
  externalLink: string;
  title: string;
  description: string;
  productImage?: string;
};

export type LinkType = "internal" | "external";
export type Platform = "linkedin" | "facebook" | "whatsapp" | "x";
export interface Block {
  block_type: string;
  is_spam: boolean;
  blocked_value: string;
  id: string;
  lead_id: string | null;
  reason: string | null;
}

export interface BlockLeadData {
  items: Block[];
  message: string;
  total: number;
}

/**
 * Request body for PATCH /api/v2/storefront/{storefront_id}/pim/products/{productId}/owner
 */
export type PIMProductOwnerUpdateArgs = {
  product_owner_id: UUID;
};

/**
 * Request body for POST /api/v2/storefront/{storefront_id}/pim/products/{productId}/subscribers
 */
export type PIMProductSubscriberArgs = {
  subscriber_ids: UUID[];
};

/**
 * Response type for subscriber list
 */
export type PIMProductSubscriber = {
  id: UUID;
  subscriber: {
    id: UUID;
    firstname: string;
    lastname: string;
    email_address: string;
    country?: string;
    business_unit?: string;
  };
};

/**
 * Response type for GET /api/v2/storefront/{storefront_id}/pim/products/{productId}/subscribers
 */
export type PIMProductSubscriberPaginatedOutput = WithPagination<{
  data: PIMProductSubscriber[];
}>;

/**
 * Product reminder kind for asset expiry notifications
 */
export type StorefrontProductEmailReminderKind =
  | "asset_expiry"
  | "price_tier_expiry";

/**
 * Single product reminder configuration
 */
export type StorefrontProductEmailReminder = {
  reminder_type: StorefrontProductEmailReminderKind;
  first_reminder: StorefrontEmailReminderNumberRange | null;
  second_reminder: StorefrontEmailReminderNumberRange | null;
  enabled: boolean;
  send_to_buyers: boolean;
  send_to_distributors: boolean;
};

/**
 * Request/Response body for PUT /api/v2/storefront/{storefront_id}/product-reminders
 */
export type StorefrontProductEmailRemindersSchema = {
  reminder_data: StorefrontProductEmailReminder[];
};

/**
 * Represents the type of the system message.
 */
export type SystemMessageKind = "info" | "alert";
/**
 * Represents the state of the system message.
 * Published: Visible to admins and seller users.
 * Archived: Not visible on dashboards, can be restored by admins.
 */
export type SystemMessageStatus = "published" | "archived";

export type SystemMessage = {
  id: UUID;
  type: SystemMessageKind;
  header: string;
  content: string;
  state: SystemMessageStatus;
  modified_at: string; // ISO 8601 date string
  archived_at: string | null; // ISO 8601 date string, null or undefined if not archived
  modified_by: string; // User who modified the message (firstname and lastname)
  created_by: string; // User who created the message (firstname and lastname)
  expires_at: string | null;
  publish_to_buyers: boolean;
  created_at: string; // ISO 8601 date string
};
/**
 * POST /api/v2/storefronts/{storefrontId}/system-messages
 */
export type CreateAlertPayload = {
  type: SystemMessageKind;
  header: string;
  content: string;
  expires_at?: string | null;
  publish_to_buyers?: boolean;
};
/**
 * PATCH /api/v2/storefronts/{storefrontId}/system-messages/{system_mssage_id}
 */
export type UpdateAlertPayload = {
  type?: SystemMessageKind;
  header?: string;
  content?: string;
};
/**
 * GET /api/v2/storefronts/{storefrontId}/system-messages
 * Query params for filtering system messages
 */
export type GetSystemMessagesParams = {
  type?: SystemMessageKind;
  state?: SystemMessageStatus;
  unread?: boolean;
};
/**
 * POST /api/v2/storefronts/{storefrontId}/system-messages/{system_message_id}/mark-read
 * Mark a system message as unread
 */
export type MarkSystemMessageUnreadResponse = {
  data: SystemMessage;
};
/**
 * GET /api/v2/storefronts/{storefrontId}/system-messages?type="alert"&state="published&unread=true"
 * this endpoint is aware of the user type that is calling it, and will return only the appropriate messages.
 */
export type GetSystemMessagesResponse = {
  data: SystemMessage[];
  pagination: {
    offset: number;
    order_by: "desc" | "asc";
    total: number;
    limit: number;
  };
};

interface Item {
  id: string | null;
  name: string;
  type?: string;
}

export interface IFilters {
  [key: string]: Item[];
}

export interface IPresetDataItem {
  display_name: string;
  filters: IFilters;
  id: string;
  name: string;
  selected: boolean;
}

export interface IPresetFilter {
  data: IPresetDataItem[];
}

// New API format for filters with wildcard and exclude support
export type FilterValue = string[] | "*";

export interface NewFilterRequest {
  filters: Record<string, FilterValue>;
  exclude_filters?: Record<string, string[]>;
}

export interface FilterMetadata {
  [filterType: string]: {
    name: string;
    id?: string | null;
  }[];
}

export type LeadModalType =
  | "convertToNew"
  | "addToExisting"
  | "forwardToDistributor"
  | "followedExternally";

export type LeadTenantType = "Buyer" | "Distributor";

export type Supplier = {
  id: UUID;
  name: string;
  supplier_code: string;
  country: string;
};
